import React, { useCallback, useEffect, useMemo, useState } from "react";

import { toast } from "react-toastify";
import {
	Area,
	AreaChart,
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	ComposedChart,
	Legend,
	Line,
	LineChart,
	Pie,
	PieChart,
	ResponsiveContainer,
	Tooltip as ReTooltip,
	XAxis,
	YAxis,
} from "recharts";
import {
	AlertTriangle,
	ArrowRightLeft,
	Award,
	BarChart3,
	CheckCircle2,
	Clock,
	Download,
	FileText,
	Forward,
	Gauge,
	Inbox,
	Layers,
	MessageSquare,
	MessageCircle,
	Radio,
	RefreshCw,
	ShieldAlert,
	Star,
	Target,
	Ticket,
	TrendingDown,
	TrendingUp,
	Users as UsersIcon,
	Zap,
	Sparkles,
	AlertCircle,
	ThumbsUp,
	ThumbsDown,
	Trophy,
} from "lucide-react";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import { cn } from "../../lib/utils";

import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Progress } from "../../components/ui/progress";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";
import { Skeleton } from "../../components/ui/skeleton";
import { Switch } from "../../components/ui/switch";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "../../components/ui/tooltip";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useThemeContext } from "../../context/DarkMode";

const ALL = "all";

const PALETTE = {
	light: {
		created: "#6366f1",
		closed: "#10b981",
		volume: "#0ea5e9",
		neutral: "#94a3b8",
		promoters: "#059669",
		passives: "#d97706",
		detractors: "#e11d48",
		categorical: ["#6366f1", "#f97316", "#10b981", "#f59e0b", "#ec4899", "#0ea5e9", "#8b5cf6", "#ef4444"],
		rating: ["#ef4444", "#f97316", "#f59e0b", "#6366f1", "#10b981"],
		grid: "#e2e8f0",
		axis: "#64748b",
		tooltipBg: "#ffffff",
		tooltipBorder: "#cbd5e1",
		tooltipText: "#0f172a",
	},
	dark: {
		created: "#818cf8",
		closed: "#34d399",
		volume: "#38bdf8",
		neutral: "#64748b",
		promoters: "#34d399",
		passives: "#fbbf24",
		detractors: "#fb7185",
		categorical: ["#818cf8", "#fb923c", "#34d399", "#fbbf24", "#f472b6", "#38bdf8", "#a78bfa", "#f87171"],
		rating: ["#f87171", "#fb923c", "#fbbf24", "#818cf8", "#34d399"],
		grid: "#1e293b",
		axis: "#94a3b8",
		tooltipBg: "#0f172a",
		tooltipBorder: "#334155",
		tooltipText: "#f8fafc",
	},
};

const EMPTY_REPORT = {
	summary: {
		total: 0,
		open: 0,
		pending: 0,
		closed: 0,
		avgFirstResponseMinutes: null,
		avgResolutionMinutes: null,
		resolutionRate: 0,
		slaFirstResponsePct: null,
		slaResolutionPct: null,
		reopenPct: null,
		reopenCount: 0,
		backlogRiskCount: 0,
		activeAgentsCount: 0,
		totalAgentsCount: 0,
		msgCountTotal: 0,
		msgCountFromCustomer: 0,
		msgCountFromAgent: 0,
		avgMessagesPerTicket: null,
		avgTurnaroundMinutes: null,
		firstResponseP50: null,
		firstResponseP75: null,
		firstResponseP90: null,
		firstResponseP95: null,
		resolutionP50: null,
		resolutionP75: null,
		resolutionP90: null,
		resolutionP95: null,
	},
	byDay: [],
	byHour: [],
	byQueue: [],
	byUser: [],
	byTag: [],
	ratings: { total: 0, average: null, distribution: [] },
	nps: { total: 0, score: null, promoters: 0, passives: 0, detractors: 0 },
	slowestTickets: [],
	messages: {
		total: 0,
		fromMe: 0,
		fromCustomer: 0,
		internal: 0,
		byType: { text: 0, image: 0, audio: 0, video: 0, document: 0, other: 0 },
		topTemplates: [],
		byWhatsapp: [],
	},
	transfers: {
		total: 0,
		byHour: new Array(24).fill(0).map((_, hour) => ({ hour, total: 0 })),
		byWeekday: ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map(
			(label, weekday) => ({ weekday, label, total: 0 })
		),
		matrix: [],
	},
	triageByWeekday: ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map(
		(label, weekday) => ({
			weekday,
			label,
			total: 0,
			closed: 0,
			slaResolutionPct: null,
			avgFirstResponseMinutes: null,
			avgResolutionMinutes: null,
		})
	),
};

const DASH = "—";

const toIsoDate = (date) => {
	const d = new Date(date);
	const month = `${d.getMonth() + 1}`.padStart(2, "0");
	const day = `${d.getDate()}`.padStart(2, "0");
	return `${d.getFullYear()}-${month}-${day}`;
};

const daysAgo = (days) => {
	const d = new Date();
	d.setDate(d.getDate() - days);
	return toIsoDate(d);
};

const isNum = (value) => typeof value === "number" && !Number.isNaN(value);

const formatMinutes = (minutes) => {
	if (!isNum(minutes)) return DASH;
	const total = Math.round(minutes);
	if (total <= 0) return "0min";
	const days = Math.floor(total / 1440);
	const hours = Math.floor((total % 1440) / 60);
	const mins = total % 60;
	if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
	if (hours > 0) return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
	return `${mins}min`;
};

const formatNumber = (value) => (isNum(value) ? value.toLocaleString("pt-BR") : DASH);

const formatPercent = (value, digits = 1) => {
	if (!isNum(value)) return DASH;
	return `${value.toFixed(digits).replace(".", ",")}%`;
};

const formatDecimal = (value, digits = 1) => {
	if (!isNum(value)) return DASH;
	return value.toFixed(digits).replace(".", ",");
};

const ratio = (part, whole) => {
	if (!isNum(part) || !isNum(whole) || whole <= 0) return null;
	return (part / whole) * 100;
};

const corStatus = (value, { goodAbove = 80, warnAbove = 55 } = {}) => {
	if (!isNum(value)) return "slate";
	if (value >= goodAbove) return "emerald";
	if (value >= warnAbove) return "amber";
	return "red";
};

const corStatusInvert = (value, { badAbove = 15, warnAbove = 5 } = {}) => {
	if (!isNum(value)) return "slate";
	if (value >= badAbove) return "red";
	if (value >= warnAbove) return "amber";
	return "emerald";
};

const ACCENT = {
	slate: {
		kpi: "text-slate-500 dark:text-slate-400",
		bgTint: "bg-slate-500/10",
		border: "border-slate-400/20",
	},
	emerald: {
		kpi: "text-emerald-600 dark:text-emerald-400",
		bgTint: "bg-emerald-500/10",
		border: "border-emerald-400/30",
	},
	amber: {
		kpi: "text-amber-600 dark:text-amber-400",
		bgTint: "bg-amber-500/10",
		border: "border-amber-400/30",
	},
	red: {
		kpi: "text-red-600 dark:text-red-400",
		bgTint: "bg-red-500/10",
		border: "border-red-400/30",
	},
	blue: {
		kpi: "text-blue-600 dark:text-blue-400",
		bgTint: "bg-blue-500/10",
		border: "border-blue-400/30",
	},
	violet: {
		kpi: "text-violet-600 dark:text-violet-400",
		bgTint: "bg-violet-500/10",
		border: "border-violet-400/30",
	},
};

// Gauge radial simplificado em SVG — sem pacotes externos. Mostra 0-100 em 3 quartos
// de círculo. Cores por faixa. Valor -> % exibida no centro.
const GaugeRadial = ({ value, label, size = 160, thickness = 14 }) => {
	const v = isNum(value) ? Math.max(0, Math.min(100, value)) : 0;
	const tone = corStatus(v);
	const stroke =
		tone === "emerald"
			? "#10b981"
			: tone === "amber"
				? "#f59e0b"
				: tone === "red"
					? "#ef4444"
					: "#64748b";
	const r = (size - thickness) / 2;
	const cx = size / 2;
	const cy = size / 2 + size * 0.1;
	// 3/4 de círculo = -225° → +45° → angulo final 45°→180→315 total = 270°
	const startAngle = 135;
	const endAngle = 405;
	const toXY = (ang) => {
		const rad = (ang * Math.PI) / 180;
		return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
	};
	const a0 = toXY(startAngle);
	const a1 = toXY(endAngle);
	const angAtual = startAngle + (v / 100) * (endAngle - startAngle);
	const aNow = toXY(angAtual);
	const largeArc = endAngle - startAngle > 180 ? 1 : 0;
	const largeArcNow = angAtual - startAngle > 180 ? 1 : 0;
	const bgPath = `M ${a0.x} ${a0.y} A ${r} ${r} 0 ${largeArc} 1 ${a1.x} ${a1.y}`;
	const valPath =
		v <= 0
			? ""
			: `M ${a0.x} ${a0.y} A ${r} ${r} 0 ${largeArcNow} 1 ${aNow.x} ${aNow.y}`;
	return (
		<div className="flex flex-col items-center gap-2">
			<div className="relative" style={{ width: size, height: size }}>
				<svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
					<path
						d={bgPath}
						fill="none"
						stroke="#cbd5e1"
						strokeOpacity="0.35"
						strokeWidth={thickness}
						strokeLinecap="round"
					/>
					{valPath && (
						<path
							d={valPath}
							fill="none"
							stroke={stroke}
							strokeWidth={thickness}
							strokeLinecap="round"
						/>
					)}
				</svg>
				<div
					className="absolute inset-0 flex flex-col items-center justify-center pt-4">
					<p className="text-3xl font-bold tracking-tight tabular-nums">
						{isNum(value) ? `${Math.round(value)}%` : DASH}
					</p>
					<p className="text-xs text-muted-foreground">{label}</p>
				</div>
			</div>
		</div>
	);
};

const periodosDiferentes = (a, b) =>
	a?.startDate !== b?.startDate || a?.endDate !== b?.endDate;

const subtrairPeriodo = ({ startDate, endDate }) => {
	if (!startDate || !endDate) return { startDate, endDate };
	const s = new Date(startDate);
	const e = new Date(endDate);
	const dias = Math.max(1, Math.round((e - s) / 86400000) + 1);
	const s2 = new Date(s);
	s2.setDate(s2.getDate() - dias);
	const e2 = new Date(e);
	e2.setDate(e2.getDate() - dias);
	return { startDate: toIsoDate(s2), endDate: toIsoDate(e2), dias };
};

const formatDayLabel = (value) => {
	if (!value || typeof value !== "string") return "";
	const parts = value.split("-");
	if (parts.length < 3) return value;
	return `${parts[2]}/${parts[1]}`;
};

const csvCell = (value) => {
	if (value === null || value === undefined) return "";
	const text = String(value).replace(/"/g, '""');
	return /[";\n]/.test(text) ? `"${text}"` : text;
};

const csvRow = (cells) => cells.map(csvCell).join(";");

const EmptyState = ({ children }) => (
	<div className="flex h-[280px] flex-col items-center justify-center gap-2 text-center">
		<Inbox className="h-8 w-8 text-muted-foreground/60" />
		<p className="text-sm text-muted-foreground">{children}</p>
	</div>
);

const Panel = ({ title, description, action, children, className }) => (
	<div className={`rounded-xl border bg-card p-5 ${className || ""}`}>
		<div className="mb-4 flex items-start justify-between gap-3">
			<div>
				<h2 className="text-sm font-semibold text-foreground">{title}</h2>
				{description ? (
					<p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
				) : null}
			</div>
			{action}
		</div>
		{children}
	</div>
);

const KpiCard = ({ icon: Icon, label, value, hint, accent, onClick, progress }) => {
	const accentBg = (() => {
		if (!accent) return "bg-slate-100 text-slate-600 ring-slate-200";
		if (accent.includes("emerald")) return "bg-emerald-50 text-emerald-600 ring-emerald-100";
		if (accent.includes("sky") || accent.includes("blue")) return "bg-sky-50 text-sky-600 ring-sky-100";
		if (accent.includes("rose") || accent.includes("red")) return "bg-rose-50 text-rose-600 ring-rose-100";
		if (accent.includes("amber") || accent.includes("yellow")) return "bg-amber-50 text-amber-600 ring-amber-100";
		if (accent.includes("violet") || accent.includes("purple")) return "bg-violet-50 text-violet-600 ring-violet-100";
		if (accent.includes("indigo")) return "bg-indigo-50 text-indigo-600 ring-indigo-100";
		return "bg-slate-50 text-slate-600 ring-slate-200";
	})();
	const barColor = (() => {
		if (!accent) return "bg-slate-200";
		if (accent.includes("emerald")) return "bg-emerald-500";
		if (accent.includes("sky") || accent.includes("blue")) return "bg-sky-500";
		if (accent.includes("rose") || accent.includes("red")) return "bg-rose-500";
		if (accent.includes("amber") || accent.includes("yellow")) return "bg-amber-500";
		if (accent.includes("violet") || accent.includes("purple")) return "bg-violet-500";
		if (accent.includes("indigo")) return "bg-indigo-500";
		return "bg-slate-400";
	})();
	const prog = typeof progress === "number" ? Math.max(0, Math.min(100, progress)) : null;
	return (
		<div
			className={cn(
				"group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_-1px_rgba(15,23,42,0.04)] transition-all duration-200",
				onClick ? "cursor-pointer hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-[0_8px_16px_-8px_rgba(15,23,42,0.15)]" : ""
			)}
			onClick={onClick}>
			<div className="flex items-start gap-3">
				<div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ring-1", accentBg)}>
					<Icon className="h-5 w-5" />
				</div>
				<div className="min-w-0 flex-1">
					<div className="truncate text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
					<p className="mt-1.5 text-2xl font-bold tracking-tight text-foreground tabular-nums">{value}</p>
					{hint ? <p className="mt-1 text-xs text-muted-foreground/90 leading-snug">{hint}</p> : null}
				</div>
			</div>
			<div className="pointer-events-none mt-3 h-1 w-full overflow-hidden rounded-full bg-slate-100">
				<div
					className={cn("h-full rounded-full transition-[width] duration-700", barColor)}
					style={{ width: prog != null ? `${prog}%` : (accent ? "42%" : "18%") }}
				/>
			</div>
		</div>
	);
};

const Stars = ({ value }) => {
	if (!isNum(value)) return <span className="text-muted-foreground">{DASH}</span>;
	const rounded = Math.round(value);
	return (
		<span className="inline-flex items-center gap-1">
			<span className="inline-flex">
				{[1, 2, 3, 4, 5].map((i) => (
					<Star
						key={i}
						className={`h-3.5 w-3.5 ${
							i <= rounded
								? "fill-amber-400 text-amber-400"
								: "text-muted-foreground/40"
						}`}
					/>
				))}
			</span>
			<span className="text-xs text-muted-foreground">{formatDecimal(value, 1)}</span>
		</span>
	);
};

const Reports = () => {
	const themeContext = useThemeContext();
	const darkMode = !!(themeContext && themeContext.darkMode);
	const colors = darkMode ? PALETTE.dark : PALETTE.light;

	const [startDate, setStartDate] = useState(daysAgo(30));
	const [endDate, setEndDate] = useState(toIsoDate(new Date()));
	const [queueId, setQueueId] = useState(ALL);
	const [userId, setUserId] = useState(ALL);
	const [tagId, setTagId] = useState(ALL);

	const [queues, setQueues] = useState([]);
	const [users, setUsers] = useState([]);
	const [tags, setTags] = useState([]);

	const [report, setReport] = useState(null);
	const [previousReport, setPreviousReport] = useState(null);
	const [loading, setLoading] = useState(true);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [loadingPrevious, setLoadingPrevious] = useState(false);
	const [showPrevious, setShowPrevious] = useState(false);
	const [prevRequestParams, setPrevRequestParams] = useState(null);
	const [ai, setAi] = useState(null);
	const [loadingAi, setLoadingAi] = useState(false);
	const [showAdvanced, setShowAdvanced] = useState(false);

	const chartTooltipStyle = useMemo(
		() => ({
			backgroundColor: colors.tooltipBg,
			border: `1px solid ${colors.tooltipBorder}`,
			borderRadius: 8,
			fontSize: 12,
			color: colors.tooltipText,
		}),
		[colors]
	);

	useEffect(() => {
		let mounted = true;
		(async () => {
			setLoading(true);
			try {
				const params = buildParams(startDate, endDate);
				// STALE-WHILE-REVALIDATE: primeiro load com 4 requests EM PARALELO
				// (queues + users + tags + reports) para reduzir latência total.
				const [queuesRes, usersRes, tagsRes, reportRes] = await Promise.all([
					api.get("/queue"),
					api.get("/users"),
					api.get("/tags"),
					api.get("/reports/tickets", { params }),
				]);
				if (!mounted) return;
				setQueues(Array.isArray(queuesRes.data) ? queuesRes.data : []);
				setTags(Array.isArray(tagsRes.data) ? tagsRes.data : []);
				setUsers(
					usersRes.data && Array.isArray(usersRes.data.users)
						? usersRes.data.users
						: []
				);
				setReport({ ...EMPTY_REPORT, ...(reportRes.data || {}) });
				setPrevRequestParams({ startDate, endDate, queueId, userId, tagId });
			} catch (err) {
				if (mounted) {
					toastError(err);
					setReport(EMPTY_REPORT);
				}
			} finally {
				if (mounted) setLoading(false);
			}
		})();
		return () => {
			mounted = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const buildParams = useCallback(
		(startDate, endDate) => {
			const params = {};
			if (startDate) params.startDate = startDate;
			if (endDate) params.endDate = endDate;
			if (queueId !== ALL) params.queueId = queueId;
			if (userId !== ALL) params.userId = userId;
			if (tagId !== ALL) params.tagId = tagId;
			return params;
		},
		[queueId, userId, tagId]
	);

	const fetchReport = useCallback(async () => {
		// STALE-WHILE-REVALIDATE:
		// • Se NÃO tem report ainda (raro) → loading=true + skeleton.
		// • Se JÁ tem report (refresh manual / aplicar filtro) → mantém dados
		//   antigos na tela com isRefreshing=true (opacity-60) e busca novo em background.
		if (!report) setLoading(true);
		else setIsRefreshing(true);
		try {
			const params = buildParams(startDate, endDate);
			const { data } = await api.get("/reports/tickets", { params });
			setReport({ ...EMPTY_REPORT, ...(data || {}) });
			setPrevRequestParams({ startDate, endDate, queueId, userId, tagId });
		} catch (err) {
			toastError(err);
			setReport(EMPTY_REPORT);
		} finally {
			setLoading(false);
			setIsRefreshing(false);
		}
	}, [buildParams, report, startDate, endDate, queueId, userId, tagId]);

	const fetchPrevious = useCallback(async () => {
		const anterior = subtrairPeriodo({ startDate, endDate });
		setLoadingPrevious(true);
		try {
			const params = buildParams(anterior.startDate, anterior.endDate);
			const { data } = await api.get("/reports/tickets", { params });
			setPreviousReport({ ...EMPTY_REPORT, ...(data || {}) });
		} catch (err) {
			toastError(err);
			setPreviousReport(EMPTY_REPORT);
		} finally {
			setLoadingPrevious(false);
		}
	}, [buildParams, startDate, endDate]);

	useEffect(() => {
		if (showPrevious) {
			fetchPrevious();
		} else {
			setPreviousReport(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showPrevious, startDate, endDate, queueId, userId, tagId]);

	const applyPreset = (days) => {
		setStartDate(days === 0 ? toIsoDate(new Date()) : daysAgo(days));
		setEndDate(toIsoDate(new Date()));
	};

	const abrirFiltro = (query) => {
		try {
			const target = `/tickets?${new URLSearchParams(query).toString()}`;
			window.location.hash = target;
		} catch (_) {}
	};

	const abrirTicket = (ticketId) => {
		try {
			window.location.hash = `/tickets/${ticketId}`;
		} catch (_) {}
	};

	const data = report || EMPTY_REPORT;
	const prev = previousReport || null;
	const summary = data.summary || EMPTY_REPORT.summary;
	const prevSummary = prev?.summary || null;
	const ratings = data.ratings || EMPTY_REPORT.ratings;
	const nps = data.nps || EMPTY_REPORT.nps;
	const byDay = Array.isArray(data.byDay) ? data.byDay : [];
	const byHour = Array.isArray(data.byHour) ? data.byHour : [];
	const byQueue = Array.isArray(data.byQueue) ? data.byQueue : [];
	const byUser = Array.isArray(data.byUser) ? data.byUser : [];
	const byTag = Array.isArray(data.byTag) ? data.byTag : [];
	const slowestTickets = Array.isArray(data.slowestTickets) ? data.slowestTickets : [];
	const messagesBlock = data.messages || EMPTY_REPORT.messages;
	const transfersBlock = data.transfers || EMPTY_REPORT.transfers;
	const triageByWeekday = Array.isArray(data.triageByWeekday)
		? data.triageByWeekday
		: EMPTY_REPORT.triageByWeekday;
	const prevByDay = prev?.byDay || [];
	const prevByHour = prev?.byHour || [];

	const sortedUsers = useMemo(
		() => [...byUser].sort((a, b) => (b.total || 0) - (a.total || 0)),
		[byUser]
	);
	const sortedQueues = useMemo(
		() => [...byQueue].sort((a, b) => (b.total || 0) - (a.total || 0)),
		[byQueue]
	);
	const sortedTags = useMemo(
		() => [...byTag].sort((a, b) => (b.total || 0) - (a.total || 0)),
		[byTag]
	);

	const queueTotal = useMemo(
		() => sortedQueues.reduce((acc, item) => acc + (item.total || 0), 0),
		[sortedQueues]
	);

	const critResumo = useMemo(() => {
		const parts = [];
		try {
			const s = new Date(startDate);
			const e = new Date(endDate);
			const fmt = (d) =>
				`${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1)
					.toString()
					.padStart(2, "0")}/${d.getFullYear()}`;
			parts.push(`${fmt(s)} → ${fmt(e)}`);
		} catch (_) {}
		if (queueId && queueId !== ALL) {
			const q = queues.find((x) => String(x.id) === String(queueId));
			parts.push(q?.name ? `Fila: ${q.name}` : `Fila #${queueId}`);
		}
		if (userId && userId !== ALL) {
			const u = users.find((x) => String(x.id) === String(userId));
			parts.push(u?.name ? `Atendente: ${u.name}` : `Atendente #${userId}`);
		}
		if (tagId && tagId !== ALL) {
			const t = tags.find((x) => String(x.id) === String(tagId));
			parts.push(t?.name ? `Tag: ${t.name}` : `Tag #${tagId}`);
		}
		if (!parts.length) return "sem filtros";
		return parts.join(" · ");
	}, [startDate, endDate, queueId, userId, tagId, queues, users, tags]);

	const hourData = useMemo(() => {
		const prevMap = new Map(prevByHour.map((x) => [x.hour, x]));
		return byHour.map((item) => {
			const p = prevMap.get(item.hour);
			return {
				...item,
				label: `${`${item.hour}`.padStart(2, "0")}h`,
				prevTotal: p ? p.total ?? 0 : null,
				prevCreated: p ? p.created ?? 0 : null,
				prevClosed: p ? p.closed ?? 0 : null,
			};
		});
	}, [byHour, prevByHour]);

	const dayData = useMemo(() => {
		// Período anterior corresponde ao dia "paralelo" (mesmo índice dentro da janela).
		const byDate = new Map(prevByDay.map((x) => [x.date, x]));
		return byDay.map((item, idx) => {
			const paralelo = prevByDay[idx];
			const match = paralelo || byDate.get(item.date);
			return {
				...item,
				label: formatDayLabel(item.date),
				prevTotal: match ? match.total ?? 0 : null,
				prevClosed: match ? match.closed ?? 0 : null,
				prevCreated: match ? match.created ?? 0 : null,
				prevBacklogEndOfDay: match ? match.backlogEndOfDay ?? null : null,
			};
		});
	}, [byDay, prevByDay]);

	const userChartData = useMemo(
		() => sortedUsers.slice(0, 12).map((item) => ({ name: item.name, total: item.total || 0 })),
		[sortedUsers]
	);

	const ratingData = useMemo(() => {
		const distribution = Array.isArray(ratings.distribution) ? ratings.distribution : [];
		return distribution.map((item) => ({
			label: `${item.rate} ★`,
			rate: item.rate,
			total: item.total || 0,
		}));
	}, [ratings]);

	const npsData = useMemo(
		() => [
			{ name: "Promotores", total: nps.promoters || 0, color: colors.promoters },
			{ name: "Neutros", total: nps.passives || 0, color: colors.passives },
			{ name: "Detratores", total: nps.detractors || 0, color: colors.detractors },
		],
		[nps, colors]
	);

	const hasRatings = ratingData.some((item) => item.total > 0);

	const handleExportCsv = () => {
		if (!report) {
			toast.info("Nenhum relatório carregado para exportar.");
			return;
		}

		const lines = [];
		lines.push(csvRow(["Relatório de tickets"]));
		lines.push(csvRow(["Período", `${startDate} a ${endDate}`]));
		lines.push("");

		lines.push(csvRow(["Resumo", "Valor"]));
		lines.push(csvRow(["Total de tickets", summary.total || 0]));
		lines.push(csvRow(["Em atendimento", summary.open || 0]));
		lines.push(csvRow(["Aguardando", summary.pending || 0]));
		lines.push(csvRow(["Resolvidos", summary.closed || 0]));
		lines.push(csvRow(["Taxa de resolução", formatPercent(summary.resolutionRate)]));
		lines.push(csvRow(["Tempo médio 1ª resposta", formatMinutes(summary.avgFirstResponseMinutes)]));
		lines.push(csvRow(["Tempo médio de resolução", formatMinutes(summary.avgResolutionMinutes)]));
		lines.push(csvRow(["Avaliação média", formatDecimal(ratings.average, 2)]));
		lines.push(csvRow(["NPS", isNum(nps.score) ? formatDecimal(nps.score, 0) : DASH]));
		lines.push(csvRow(["SLA 1ª resposta (%)", formatPercent(summary.slaFirstResponsePct)]));
		lines.push(csvRow(["SLA resolução (%)", formatPercent(summary.slaResolutionPct)]));
		lines.push(csvRow(["Taxa de reabertura (%)", formatPercent(summary.reopenPct)]));
		lines.push(csvRow(["Reaberturas (qtd)", summary.reopenCount || 0]));
		lines.push(csvRow(["Backlog em risco (qtd)", summary.backlogRiskCount || 0]));
		lines.push(csvRow(["Atendentes ativos", `${summary.activeAgentsCount || 0} de ${summary.totalAgentsCount || 0}`]));
		lines.push(csvRow(["Mensagens (total)", summary.msgCountTotal || 0]));
		lines.push(csvRow(["Mensagens / ticket (média)", formatDecimal(summary.avgMessagesPerTicket, 1)]));
		lines.push(csvRow(["Turnaround médio atendimento", formatMinutes(summary.avgTurnaroundMinutes)]));
		lines.push("");

		lines.push(csvRow(["SLA & Tempos - Percentis (1ª resposta, minutos)"]));
		lines.push(csvRow(["P50", "P75", "P90", "P95"]));
		lines.push(csvRow([
			formatMinutes(summary.firstResponseP50),
			formatMinutes(summary.firstResponseP75),
			formatMinutes(summary.firstResponseP90),
			formatMinutes(summary.firstResponseP95),
		]));
		lines.push("");
		lines.push(csvRow(["SLA & Tempos - Percentis (Resolução, minutos)"]));
		lines.push(csvRow(["P50", "P75", "P90", "P95"]));
		lines.push(csvRow([
			formatMinutes(summary.resolutionP50),
			formatMinutes(summary.resolutionP75),
			formatMinutes(summary.resolutionP90),
			formatMinutes(summary.resolutionP95),
		]));
		lines.push("");

		lines.push(csvRow(["Atendentes"]));
		lines.push(csvRow([
			"Atendente",
			"Total",
			"Resolvidos",
			"Taxa resolução",
			"Avaliação média",
			"1ª resposta (média)",
			"Resolução (média)",
			"SLA 1ª resp %",
			"SLA resolução %",
			"Transferências recebidas",
			"Transferências efetuadas",
			"Mensagens enviadas",
			"Respostas rápidas usadas",
			"Backlog (média fim de dia)",
		]));
		sortedUsers.forEach((item) => {
			lines.push(csvRow([
				item.name,
				item.total || 0,
				item.closed || 0,
				formatPercent(ratio(item.closed, item.total)),
				formatDecimal(item.avgRating, 2),
				formatMinutes(item.avgFirstResponseMinutes),
				formatMinutes(item.avgResolutionMinutes),
				formatPercent(item.slaFirstResponsePct),
				formatPercent(item.slaResolutionPct),
				item.transfersIn || 0,
				item.transfersOut || 0,
				item.messagesFromMe || 0,
				item.templatesUsedCount || 0,
				formatNumber(item.avgBacklogEndOfDay),
			]));
		});
		if (!sortedUsers.length) lines.push(csvRow(["Sem dados"]));
		lines.push("");

		lines.push(csvRow(["Filas"]));
		lines.push(csvRow([
			"Fila",
			"Total",
			"% do total",
			"Em atendimento",
			"Aguardando",
			"Resolvidos",
			"Taxa resolução",
			"Espera média (fila, min)",
			"Resolução média",
			"SLA 1ª resposta %",
			"SLA resolução %",
		]));
		sortedQueues.forEach((item) => {
			lines.push(csvRow([
				item.name,
				item.total || 0,
				formatPercent(ratio(item.total, queueTotal)),
				item.open || 0,
				item.pending || 0,
				item.closed || 0,
				formatPercent(ratio(item.closed, item.total)),
				formatMinutes(item.avgWaitMinutes),
				formatMinutes(item.avgResolutionMinutes),
				formatPercent(item.slaFirstResponsePct),
				formatPercent(item.slaResolutionPct),
			]));
		});
		if (!sortedQueues.length) lines.push(csvRow(["Sem dados"]));
		lines.push("");

		lines.push(csvRow(["Etiquetas"]));
		lines.push(csvRow([
			"Etiqueta",
			"Total",
			"Fechados",
			"Taxa resolução",
			"Resolução média (min)",
		]));
		sortedTags.forEach((item) => {
			lines.push(csvRow([
				item.name,
				item.total || 0,
				item.closedCount || 0,
				formatPercent(item.resolutionRate),
				formatMinutes(item.avgResolutionMinutes),
			]));
		});
		if (!sortedTags.length) lines.push(csvRow(["Sem dados"]));
		lines.push("");

		lines.push(csvRow(["Mensagens e Canais"]));
		lines.push(csvRow(["Total", "De atendente", "De cliente", "Interno"]));
		lines.push(csvRow([
			messagesBlock.total || 0,
			messagesBlock.fromMe || 0,
			messagesBlock.fromCustomer || 0,
			messagesBlock.internal || 0,
		]));
		lines.push("");
		lines.push(csvRow(["Top Respostas Rápidas"]));
		lines.push(csvRow(["Posição", "Atalho", "Nome", "Usos"]));
		(Array.isArray(messagesBlock.topTemplates) ? messagesBlock.topTemplates : []).forEach((t, i) => {
			lines.push(csvRow([i + 1, t.shortcut || DASH, t.name || DASH, t.count || 0]));
		});
		lines.push("");

		lines.push(csvRow(["Triagem por dia da semana"]));
		lines.push(csvRow([
			"Dia",
			"Total",
			"Fechados",
			"SLA resolução %",
			"1ª resposta média (min)",
			"Resolução média (min)",
		]));
		triageByWeekday.forEach((d) => {
			lines.push(csvRow([
				d.label,
				d.total || 0,
				d.closed || 0,
				formatPercent(d.slaResolutionPct),
				formatMinutes(d.avgFirstResponseMinutes),
				formatMinutes(d.avgResolutionMinutes),
			]));
		});
		lines.push("");

		lines.push(csvRow(["Top 10 tickets mais demorados"]));
		lines.push(csvRow([
			"Ticket",
			"Cliente",
			"Número",
			"Fila",
			"Atendente",
			"Criado em",
			"Fechado em",
			"Resolução (min)",
			"Tags",
		]));
		slowestTickets.forEach((t) => {
			lines.push(csvRow([
				t.ticketId,
				t.customerName || DASH,
				t.customerNumber || DASH,
				t.queueName || DASH,
				t.userName || DASH,
				t.createdAt ? t.createdAt.substring(0, 19).replace("T", " ") : DASH,
				t.closedAt ? t.closedAt.substring(0, 19).replace("T", " ") : DASH,
				t.resolutionMinutes || 0,
				Array.isArray(t.tags) ? t.tags.join(", ") : DASH,
			]));
		});

		const csv = `﻿${lines.join("\r\n")}`;
		const blobCsv = new Blob([csv], { type: "text/csv;charset=utf-8;" });
		triggerDownload(`relatorio-tickets-${toIsoDate(new Date())}.csv`, blobCsv);
		toast.success("Relatório exportado com sucesso!");
	};

	const triggerDownload = (filename, blob) => {
		const objectUrl = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = objectUrl;
		a.download = filename;
		a.rel = "noopener";
		a.style.display = "none";
		let clicked = false;
		try {
			document.body.appendChild(a);
			try {
				a.click();
				clicked = true;
			} catch (_) {
				window.location.href = objectUrl;
				clicked = true;
			}
		} finally {
			try { document.body.removeChild(a); } catch (_) {}
			setTimeout(() => {
				try { URL.revokeObjectURL(objectUrl); } catch (_) {}
			}, 8000);
		}
		return clicked;
	};

	const handleExportPdf = async () => {
		try {
			const params = buildParams(startDate, endDate);
			const response = await api.get("/reports/tickets", {
				params: { ...params, format: "pdf" },
				responseType: "blob",
				headers: {
					Accept: "application/pdf",
					"X-No-Compression": "1",
					"Cache-Control": "no-store, no-transform",
				},
				timeout: 0,
				withCredentials: true,
			});
			const rawBlob = response?.data;
			if (!rawBlob || rawBlob.size === 0) {
				throw new Error("PDF vazio — tente novamente.");
			}
			const arrBuff = await rawBlob.arrayBuffer();
			const bytes = new Uint8Array(arrBuff);
			const head = String.fromCharCode(...bytes.slice(0, 8));
			if (!head.startsWith("%PDF-")) {
				const txt = new TextDecoder("utf-8", { fatal: false }).decode(bytes.slice(0, 300));
				console.warn("[PDF] bytes inesperados:", txt);
				throw new Error("Arquivo não é um PDF válido.");
			}
			const pdfBlob = new Blob([bytes.buffer || bytes], { type: "application/pdf" });
			const filename = (() => {
				const cd = response.headers && response.headers["content-disposition"];
				const m = cd && cd.match(/filename\s*=\s*"([^"]+)"/);
				if (m && m[1]) return m[1];
				return `relatorio-tickets-${toIsoDate(new Date())}.pdf`;
			})();
			triggerDownload(filename, pdfBlob);
			toast.success("PDF exportado com sucesso!");
		} catch (err) {
			toastError(err);
		}
	};

	const renderChartSkeleton = () => (
		<div className="grid gap-4 md:grid-cols-2">
			<Skeleton className="h-[320px] w-full rounded-xl" />
			<Skeleton className="h-[320px] w-full rounded-xl" />
		</div>
	);

	const isHeatmap = (item) =>
		Array.isArray(item?.byHour) && item.byHour.length > 0;

	const buildByHour = (item) => {
		const raw = Array.isArray(item?.byHour) ? item.byHour : [];
		const arr = new Array(24).fill(0).map((_, hour) => ({
			hour,
			total: 0,
		}));
		raw.forEach((h) => {
			if (h && h.hour >= 0 && h.hour < 24) {
				arr[h.hour].total = (h.total ?? 0) + (arr[h.hour].total || 0);
			}
		});
		let mx = 0;
		arr.forEach((h) => {
			if (h.total > mx) mx = h.total;
		});
		(item || {}).heatmax = mx;
		return arr;
	};

	const heatColor = (value, min, max) => {
		if (!isNum(value) || max - min <= 0) return "#e2e8f0";
		const pct = Math.max(0, Math.min(1, (value - min) / (max - min)));
		// verde 200 → 600
		if (pct <= 0.25) return "#bbf7d0";
		if (pct <= 0.5) return "#86efac";
		if (pct <= 0.75) return "#4ade80";
		return "#16a34a";
	};

	return (
		<TooltipProvider>
		<div className={`transition-opacity duration-200 ${isRefreshing && !loading ? "opacity-60" : "opacity-100"}`}>
		<MainContainer>
			<MainHeader>
				<Title>Relatórios</Title>
				<MainHeaderButtonsWrapper>
					<Button variant="outline" onClick={fetchReport} disabled={loading}>
						<RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
						Atualizar
					</Button>
					<Button
						variant="outline"
						onClick={handleExportPdf}
						disabled={loading || !report}>
						<FileText className="h-4 w-4" />
						Exportar PDF
					</Button>
					<Button
						variant="outline"
						onClick={handleExportCsv}
						disabled={loading || !report}>
						<Download className="h-4 w-4" />
						Exportar CSV
					</Button>
				</MainHeaderButtonsWrapper>
			</MainHeader>

			<div className="flex-1 space-y-4 overflow-auto px-4 pb-4 sm:px-6 sm:pb-6">
				{/* Filtros */}
				<div className="rounded-xl border bg-card p-4">
					<div className="grid gap-3 lg:grid-cols-[repeat(4,minmax(0,1fr))_auto] lg:items-end">
						<div className="space-y-1.5">
							<Label htmlFor="reports-start">De</Label>
							<Input
								id="reports-start"
								type="date"
								value={startDate}
								max={endDate}
								onChange={(e) => setStartDate(e.target.value)}
							/>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="reports-end">Até</Label>
							<Input
								id="reports-end"
								type="date"
								value={endDate}
								min={startDate}
								onChange={(e) => setEndDate(e.target.value)}
							/>
						</div>
						<div className="space-y-1.5">
							<Label>Fila</Label>
							<Select value={queueId} onValueChange={setQueueId}>
								<SelectTrigger>
									<SelectValue placeholder="Todas as filas" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={ALL}>Todas as filas</SelectItem>
									{queues.map((queue) => (
										<SelectItem key={queue.id} value={String(queue.id)}>
											{queue.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-1.5">
							<Label>Atendente</Label>
							<Select value={userId} onValueChange={setUserId}>
								<SelectTrigger>
									<SelectValue placeholder="Todos os atendentes" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={ALL}>Todos os atendentes</SelectItem>
									{users.map((user) => (
										<SelectItem key={user.id} value={String(user.id)}>
											{user.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-1.5">
							<Label>Etiqueta</Label>
							<Select value={tagId} onValueChange={setTagId}>
								<SelectTrigger>
									<SelectValue placeholder="Todas as etiquetas" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={ALL}>Todas as etiquetas</SelectItem>
									{tags.map((tag) => (
										<SelectItem key={tag.id} value={String(tag.id)}>
											{tag.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<Button onClick={fetchReport} disabled={loading} className="lg:w-28">
							Aplicar
						</Button>
					</div>
					<div className="mt-3 flex flex-wrap items-center justify-between gap-2">
						<div className="flex flex-wrap items-center gap-2">
							<span className="text-xs text-muted-foreground">
								Períodos rápidos:
							</span>
							<Button variant="outline" size="sm" onClick={() => applyPreset(0)}>
								Hoje
							</Button>
							<Button variant="outline" size="sm" onClick={() => applyPreset(7)}>
								7 dias
							</Button>
							<Button variant="outline" size="sm" onClick={() => applyPreset(30)}>
								30 dias
							</Button>
							<Button variant="outline" size="sm" onClick={() => applyPreset(90)}>
								90 dias
							</Button>
						</div>
						<div className="flex items-center gap-3 rounded-lg border border-dashed px-3 py-2">
							<Label
								htmlFor="reports-prev-toggle"
								className="text-xs text-muted-foreground">
								Comparar com período anterior
							</Label>
							<Switch
								id="reports-prev-toggle"
								checked={showPrevious}
								onCheckedChange={setShowPrevious}
							/>
							{showPrevious && (
								<Tooltip>
									<TooltipTrigger asChild>
										<span className="text-xs text-muted-foreground">
											{(() => {
											const p = subtrairPeriodo({ startDate, endDate });
											return `Anterior: ${p.startDate} a ${p.endDate}`;
										})()}
											{loadingPrevious ? " (carregando...)" : ""}
										</span>
									</TooltipTrigger>
									<TooltipContent>
										Linha tracejada nos gráficos = período imediatamente anterior, com mesma duração.
									</TooltipContent>
								</Tooltip>
							)}
						</div>
					</div>
				</div>

				{/* KPIs */}
				{loading ? (
					<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
						{Array.from({ length: 6 }).map((_, i) => (
							<Skeleton key={i} className="h-[104px] w-full rounded-xl" />
						))}
					</div>
				) : (
					<TooltipProvider>
						<div className="space-y-4">
							<div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
								<div>
									<h2 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-100">
										Painel de performance
									</h2>
									<p className="text-sm text-slate-500">
										{summary.total
											? `${formatNumber(summary.total)} tickets no período · Taxa ${formatPercent(summary.resolutionRate)}`
											: "Sem tickets no período selecionado."}
									</p>
								</div>
								<div className="flex flex-wrap items-center gap-2">
									<Badge variant="outline" className="bg-slate-50 text-slate-700">
										Critérios: {critResumo}
									</Badge>
								</div>
							</div>

							{/* 6 KPIs PRINCIPAIS — sempre visíveis */}
							<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
								<KpiCard
									icon={Ticket}
									label="Total de tickets"
									value={formatNumber(summary.total)}
									hint={
										prevSummary
											? `Anterior ${formatNumber(prevSummary.total)}`
											: "No período selecionado"
									}
									progress={100}
									onClick={() => abrirFiltro({ status: "todos" })}
								/>
								<KpiCard
									icon={BarChart3}
									label="Em atendimento"
									value={formatNumber(summary.open)}
									hint={formatPercent(ratio(summary.open, summary.total))}
									accent={ACCENT.blue.kpi}
									progress={ratio(summary.open, summary.total) * 100}
									onClick={() => abrirFiltro({ status: "open" })}
								/>
								<KpiCard
									icon={Clock}
									label="Aguardando"
									value={formatNumber(summary.pending)}
									hint={formatPercent(ratio(summary.pending, summary.total))}
									accent={summary.pending > 0 ? ACCENT.amber.kpi : ""}
									progress={ratio(summary.pending, summary.total) * 100}
									onClick={() => abrirFiltro({ status: "pending" })}
								/>
								<KpiCard
									icon={CheckCircle2}
									label="Resolvidos"
									value={formatNumber(summary.closed)}
									hint={formatPercent(ratio(summary.closed, summary.total))}
									accent={ACCENT.emerald.kpi}
									progress={ratio(summary.closed, summary.total) * 100}
									onClick={() => abrirFiltro({ status: "closed" })}
								/>
								<KpiCard
									icon={TrendingUp}
									label="Taxa de resolução"
									value={formatPercent(summary.resolutionRate)}
									hint="Resolvidos / total"
									progress={summary.resolutionRate}
									accent={
										ACCENT[
											corStatus(summary.resolutionRate, { goodAbove: 80, warnAbove: 60 })
										].kpi
									}
								/>
								<KpiCard
									icon={Clock}
									label="1ª resposta (média)"
									value={formatMinutes(summary.avgFirstResponseMinutes)}
									hint="Tempo até o primeiro retorno · meta 15min"
									progress={Math.min(100, 100 - ratio(summary.avgFirstResponseMinutes, 15) * 100)}
									accent={
										ACCENT[
											corStatus(
												100 - Math.min(100, ratio(summary.avgFirstResponseMinutes, 15) * 100),
												{ goodAbove: 80, warnAbove: 55 }
											)
										].kpi
									}
								/>
							</div>

							{/* Toggle métricas avançadas */}
							<Card className="border-slate-200/80">
								<CardContent className="flex flex-col gap-3 p-0">
									<div className="flex flex-wrap items-center justify-between gap-2 p-3">
										<div className="flex items-center gap-2">
											<span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-slate-100 text-slate-700 ring-1 ring-slate-200">
												<Layers size={16} />
											</span>
											<div>
												<h3 className="text-sm font-semibold text-slate-900">
													Métricas detalhadas
												</h3>
												<p className="text-xs text-slate-500">
													SLA, tempos percentis, reaberturas e backlog — 7 indicadores
												</p>
											</div>
										</div>
										<div className="flex items-center gap-3">
											<Label htmlFor="sw-advanced" className="sr-only">
												Métricas detalhadas
											</Label>
											<Switch
												id="sw-advanced"
												checked={showAdvanced}
												onCheckedChange={setShowAdvanced}
											/>
											<Badge variant="outline" className="bg-white">
												{showAdvanced ? "Ocultar" : "Mostrar 7"}
											</Badge>
										</div>
									</div>

									{showAdvanced ? (
										<div className="grid gap-3 border-t border-slate-200/80 p-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
											<KpiCard
												icon={CheckCircle2}
												label="Resolução (média)"
												value={formatMinutes(summary.avgResolutionMinutes)}
												hint="Abertura até o fechamento · meta 4h"
												progress={Math.min(100, 100 - ratio(summary.avgResolutionMinutes, 240) * 100)}
												accent={
													ACCENT[
														corStatus(
															100 - Math.min(100, ratio(summary.avgResolutionMinutes, 240) * 100),
															{ goodAbove: 80, warnAbove: 55 }
														)
													].kpi
												}
											/>

											<Tooltip>
												<TooltipTrigger asChild>
													<KpiCard
														icon={Target}
														label="SLA 1ª resposta"
														value={formatPercent(summary.slaFirstResponsePct)}
														hint="Meta ≤ 15 min"
														accent={ACCENT[corStatus(summary.slaFirstResponsePct)].kpi}
														progress={summary.slaFirstResponsePct}
													/>
												</TooltipTrigger>
												<TooltipContent sideOffset={4}>
													Tickets com primeira resposta do atendente dentro do SLA.
												</TooltipContent>
											</Tooltip>

											<Tooltip>
												<TooltipTrigger asChild>
													<KpiCard
														icon={Gauge}
														label="SLA Resolução"
														value={formatPercent(summary.slaResolutionPct)}
														hint="Meta ≤ 4h"
														accent={ACCENT[corStatus(summary.slaResolutionPct)].kpi}
														progress={summary.slaResolutionPct}
													/>
												</TooltipTrigger>
												<TooltipContent sideOffset={4}>
													Tickets fechados dentro da meta de 4h de resolução.
												</TooltipContent>
											</Tooltip>

											<Tooltip>
												<TooltipTrigger asChild>
													<KpiCard
														icon={Forward}
														label="Reaberturas"
														value={
															isNum(summary.reopenPct)
																? `${Math.round(summary.reopenPct)}%`
																: DASH
														}
														hint={`${formatNumber(summary.reopenCount)} ticket(s) · janela 48h`}
														accent={ACCENT[corStatusInvert(summary.reopenPct)].kpi}
														progress={Math.max(0, 100 - Number(summary.reopenPct || 0))}
														onClick={() => abrirFiltro({ status: "todos", reabertos: 1 })}
													/>
												</TooltipTrigger>
												<TooltipContent sideOffset={4}>
													Tickets fechados no período que voltaram a status diferente de fechado
													em até 48h.
												</TooltipContent>
											</Tooltip>

											<Tooltip>
												<TooltipTrigger asChild>
													<KpiCard
														icon={ShieldAlert}
														label="Backlog em risco"
														value={formatNumber(summary.backlogRiskCount)}
														hint="Sem 1ª resposta há ≥ 2h"
														accent={
															summary.backlogRiskCount > 0
																? ACCENT.red.kpi
																: ACCENT.emerald.kpi
														}
														progress={Math.max(0, 100 - ratio(summary.backlogRiskCount, summary.total) * 100 * 2)}
														onClick={() => abrirFiltro({ status: "pending", risco: 1 })}
													/>
												</TooltipTrigger>
												<TooltipContent sideOffset={4}>
													Tickets em aberto/aguardando sem nenhuma resposta do atendente há 2h
													ou mais.
												</TooltipContent>
											</Tooltip>

											<Tooltip>
												<TooltipTrigger asChild>
													<KpiCard
														icon={UsersIcon}
														label="Atendentes ativos"
														value={`${formatNumber(summary.activeAgentsCount)} de ${formatNumber(
															summary.totalAgentsCount
														)}`}
														hint="Interagiram no período"
														accent={ACCENT.violet.kpi}
														progress={ratio(summary.activeAgentsCount, summary.totalAgentsCount) * 100}
													/>
												</TooltipTrigger>
												<TooltipContent sideOffset={4}>
													Atendentes alocados vs. os que tiveram pelo menos 1 ticket ou
													mensagem.
												</TooltipContent>
											</Tooltip>

											<Tooltip>
												<TooltipTrigger asChild>
													<KpiCard
														icon={MessageCircle}
														label="Mensagens / ticket"
														value={formatDecimal(summary.avgMessagesPerTicket, 1)}
														hint={`Turnaround ${formatMinutes(summary.avgTurnaroundMinutes)}`}
														accent={ACCENT.blue.kpi}
													/>
												</TooltipTrigger>
												<TooltipContent sideOffset={4}>
													Média de mensagens (cliente + atendente) por ticket do período.
													Turnaround médio do atendimento.
												</TooltipContent>
											</Tooltip>
										</div>
									) : null}
								</CardContent>
							</Card>
						</div>
					</TooltipProvider>
				)}

				{/* Detalhamento */}
				<Tabs defaultValue="overview" className="w-full">
					<TabsList className="flex h-auto flex-wrap justify-start">
						<TabsTrigger value="overview">Visão geral</TabsTrigger>
						<TabsTrigger value="sla">SLA &amp; Tempos</TabsTrigger>
						<TabsTrigger value="messages">Mensagens &amp; Canais</TabsTrigger>
						<TabsTrigger value="agents">Atendentes</TabsTrigger>
						<TabsTrigger value="queues">Filas &amp; Etiquetas</TabsTrigger>
						<TabsTrigger value="triage">Fila &amp; Triagem</TabsTrigger>
						<TabsTrigger value="satisfaction">Satisfação</TabsTrigger>
						<TabsTrigger value="ai">IA &amp; Acompanhamento</TabsTrigger>
					</TabsList>

					{/* Visão geral */}
					<TabsContent value="overview" className="mt-4 space-y-4">
						{loading ? (
							renderChartSkeleton()
						) : (
							<div className="grid gap-4 xl:grid-cols-2">
								<Panel
									title="Tickets criados x resolvidos"
									description="Evolução diária no período. Linha tracejada = período anterior."
								>
									{dayData.length ? (
										<div className="h-[280px] w-full">
											<ResponsiveContainer width="100%" height="100%">
												<ComposedChart data={dayData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
													<defs>
														<linearGradient id="reportsCreated" x1="0" y1="0" x2="0" y2="1">
															<stop offset="5%" stopColor={colors.created} stopOpacity={0.35} />
															<stop offset="95%" stopColor={colors.created} stopOpacity={0} />
														</linearGradient>
														<linearGradient id="reportsClosed" x1="0" y1="0" x2="0" y2="1">
															<stop offset="5%" stopColor={colors.closed} stopOpacity={0.35} />
															<stop offset="95%" stopColor={colors.closed} stopOpacity={0} />
														</linearGradient>
													</defs>
													<CartesianGrid strokeDasharray="3 3" stroke={colors.grid} vertical={false} />
													<XAxis
														dataKey="label"
														tick={{ fontSize: 11, fill: colors.axis }}
														tickLine={false}
														axisLine={false}
														minTickGap={16}
													/>
													<YAxis
														allowDecimals={false}
														tick={{ fontSize: 11, fill: colors.axis }}
														tickLine={false}
														axisLine={false}
													/>
													<ReTooltip contentStyle={chartTooltipStyle} />
													<Legend wrapperStyle={{ fontSize: 12 }} />
													<Area
														type="monotone"
														dataKey="created"
														name="Criados"
														stroke={colors.created}
														strokeWidth={2}
														fill="url(#reportsCreated)"
													/>
													<Area
														type="monotone"
														dataKey="closed"
														name="Resolvidos"
														stroke={colors.closed}
														strokeWidth={2}
														fill="url(#reportsClosed)"
													/>
													{showPrevious ? (
														<>
															<Line
																type="monotone"
																dataKey="prevCreated"
																name="Criados (ant.)"
																stroke={colors.created}
																strokeDasharray="4 4"
																strokeOpacity={0.65}
																dot={false}
															/>
															<Line
																type="monotone"
																dataKey="prevClosed"
																name="Resolvidos (ant.)"
																stroke={colors.closed}
																strokeDasharray="4 4"
																strokeOpacity={0.65}
																dot={false}
															/>
														</>
													) : null}
												</ComposedChart>
											</ResponsiveContainer>
										</div>
									) : (
										<EmptyState>Nenhum ticket no período selecionado.</EmptyState>
									)}
								</Panel>

								<Panel
									title="Volume por hora do dia"
									description="Criados (barras cheias) / Fechados (empilhado). Linha = período anterior."
								>
									{hourData.length ? (
										<div className="h-[280px] w-full">
											<ResponsiveContainer width="100%" height="100%">
												<ComposedChart data={hourData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
													<CartesianGrid strokeDasharray="3 3" stroke={colors.grid} vertical={false} />
													<XAxis
														dataKey="label"
														tick={{ fontSize: 11, fill: colors.axis }}
														tickLine={false}
														axisLine={false}
														interval={1}
													/>
													<YAxis
														allowDecimals={false}
														tick={{ fontSize: 11, fill: colors.axis }}
														tickLine={false}
														axisLine={false}
													/>
													<ReTooltip cursor={{ fillOpacity: 0.08 }} contentStyle={chartTooltipStyle} />
													<Legend wrapperStyle={{ fontSize: 12 }} />
													<Bar
														dataKey="created"
														name="Criados"
														fill={colors.created}
														stackId="hvol"
														radius={[4, 4, 0, 0]}
														maxBarSize={22}
													/>
													<Bar
														dataKey="closed"
														name="Fechados"
														fill={colors.closed}
														stackId="hvol"
														radius={[4, 4, 0, 0]}
														maxBarSize={22}
													/>
													{showPrevious ? (
														<>
															<Line
																type="monotone"
																dataKey="prevCreated"
																name="Criados (ant.)"
																stroke={colors.created}
																strokeDasharray="4 4"
																strokeOpacity={0.65}
																dot={false}
															/>
															<Line
																type="monotone"
																dataKey="prevClosed"
																name="Fechados (ant.)"
																stroke={colors.closed}
																strokeDasharray="4 4"
																strokeOpacity={0.65}
																dot={false}
															/>
														</>
													) : null}
												</ComposedChart>
											</ResponsiveContainer>
										</div>
									) : (
										<EmptyState>Sem dados de volume por hora.</EmptyState>
									)}
								</Panel>
							</div>
						)}
					</TabsContent>

					{/* SLA & Tempos */}
					<TabsContent value="sla" className="mt-4 space-y-4">
						{loading ? (
							renderChartSkeleton()
						) : (
							<>
								<div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
									<Panel title="SLA 1ª Resposta" description="Meta ≤ 15 min">
										<div className="flex items-center justify-center py-2">
											<GaugeRadial
												value={summary.slaFirstResponsePct}
												label="Cumprido"
											/>
										</div>
									</Panel>
									<Panel title="SLA Resolução" description="Meta ≤ 4 h">
										<div className="flex items-center justify-center py-2">
											<GaugeRadial
												value={summary.slaResolutionPct}
												label="Cumprido"
											/>
										</div>
									</Panel>
									<Panel
										title="Percentis (1ª resposta, min)"
										description="P50 / P75 / P90 / P95">
										<div className="space-y-3 py-1">
											{[
												{ k: "P50", v: summary.firstResponseP50 },
												{ k: "P75", v: summary.firstResponseP75 },
												{ k: "P90", v: summary.firstResponseP90 },
												{ k: "P95", v: summary.firstResponseP95 },
											].map((x) => (
												<div key={x.k} className="space-y-1">
													<div className="flex items-center justify-between text-xs">
														<span className="font-medium text-muted-foreground">
															{x.k}
														</span>
														<span className="tabular-nums">
															{formatMinutes(x.v)}
														</span>
													</div>
													<Progress
														value={Math.min(
															100,
															ratio(x.v, summary.firstResponseP95 ?? x.v ?? 1)
														)}
													/>
												</div>
											))}
										</div>
									</Panel>
									<Panel
										title="Percentis (Resolução, min)"
										description="P50 / P75 / P90 / P95">
										<div className="space-y-3 py-1">
											{[
												{ k: "P50", v: summary.resolutionP50 },
												{ k: "P75", v: summary.resolutionP75 },
												{ k: "P90", v: summary.resolutionP90 },
												{ k: "P95", v: summary.resolutionP95 },
											].map((x) => (
												<div key={x.k} className="space-y-1">
													<div className="flex items-center justify-between text-xs">
														<span className="font-medium text-muted-foreground">
															{x.k}
														</span>
														<span className="tabular-nums">
															{formatMinutes(x.v)}
														</span>
													</div>
													<Progress
														value={Math.min(
															100,
															ratio(x.v, summary.resolutionP95 ?? x.v ?? 1)
														)}
													/>
												</div>
											))}
										</div>
									</Panel>
								</div>

								<Panel
									title="Backlog acumulado (fim de dia)"
									description="Soma histórica: backlog_ant + criados - fechados por dia. Linha tracejada = período anterior.">
									{dayData.length ? (
										<div className="h-[300px] w-full">
											<ResponsiveContainer width="100%" height="100%">
												<LineChart
													data={dayData}
													margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
													<CartesianGrid
														strokeDasharray="3 3"
														stroke={colors.grid}
														vertical={false}
													/>
													<XAxis
														dataKey="label"
														tick={{ fontSize: 11, fill: colors.axis }}
														tickLine={false}
														axisLine={false}
														minTickGap={16}
													/>
													<YAxis
														allowDecimals={false}
														tick={{ fontSize: 11, fill: colors.axis }}
														tickLine={false}
														axisLine={false}
													/>
													<ReTooltip contentStyle={chartTooltipStyle} />
													<Legend wrapperStyle={{ fontSize: 12 }} />
													<Line
														type="monotone"
														dataKey="backlogEndOfDay"
														name="Backlog fim do dia"
														stroke={colors.created}
														strokeWidth={2.2}
														dot={{ r: 2 }}
														activeDot={{ r: 4 }}
													/>
													{showPrevious ? (
														<Line
															type="monotone"
															dataKey="prevBacklogEndOfDay"
															name="Backlog (anterior)"
															stroke={colors.created}
															strokeDasharray="4 4"
															strokeOpacity={0.65}
															dot={false}
														/>
													) : null}
												</LineChart>
											</ResponsiveContainer>
										</div>
									) : (
										<EmptyState>Sem dias suficientes no período.</EmptyState>
									)}
								</Panel>

								<Panel
									title="Top 10 tickets mais demorados"
									description="Maiores tempos de resolução do período. Clique em 'Abrir' para abrir o ticket.">
									{slowestTickets.length ? (
										<div className="overflow-auto">
											<Table>
												<TableHeader className="sticky top-0 z-10 bg-card">
													<TableRow>
														<TableHead>ID</TableHead>
														<TableHead>Cliente</TableHead>
														<TableHead>Fila</TableHead>
														<TableHead>Atendente</TableHead>
														<TableHead className="text-right">
															Resolução (min)
														</TableHead>
														<TableHead>Fechado em</TableHead>
														<TableHead>Tags</TableHead>
														<TableHead className="text-right">Ação</TableHead>
													</TableRow>
												</TableHeader>
												<TableBody>
													{slowestTickets.map((t) => (
														<TableRow key={t.ticketId}>
															<TableCell className="font-medium tabular-nums">
																#{t.ticketId}
															</TableCell>
															<TableCell>
																<div>
																	<p className="font-medium">
																		{t.customerName || "Sem nome"}
																	</p>
																	{t.customerNumber ? (
																		<p className="text-xs text-muted-foreground">
																			{t.customerNumber}
																		</p>
																	) : null}
																</div>
															</TableCell>
															<TableCell>{t.queueName || DASH}</TableCell>
															<TableCell>{t.userName || DASH}</TableCell>
															<TableCell className="text-right tabular-nums">
																{formatNumber(t.resolutionMinutes)}
															</TableCell>
															<TableCell className="tabular-nums text-xs text-muted-foreground">
																{t.closedAt
																	? t.closedAt.substring(0, 16).replace("T", " ")
																	: DASH}
															</TableCell>
															<TableCell>
																<div className="flex flex-wrap gap-1">
																	{Array.isArray(t.tags) && t.tags.length
																		? t.tags.map((tg) => (
																				<Badge
																					key={tg}
																					variant="secondary"
																					className="text-[10px]">
																					{tg}
																				</Badge>
																			))
																		: DASH}
																</div>
															</TableCell>
															<TableCell className="text-right">
																<Button
																	size="sm"
																	variant="outline"
																	onClick={() =>
																		abrirTicket(t.ticketId)
																	}>
																	Abrir
																</Button>
															</TableCell>
														</TableRow>
													))}
												</TableBody>
											</Table>
										</div>
									) : (
										<EmptyState>
											Não foi possível identificar tickets fechados no período.
										</EmptyState>
									)}
								</Panel>
							</>
						)}
					</TabsContent>

					{/* Mensagens & Canais */}
					<TabsContent value="messages" className="mt-4 space-y-4">
						{loading ? (
							renderChartSkeleton()
						) : (
							<>
								<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
									<KpiCard
										icon={MessageSquare}
										label="Mensagens total"
										value={formatNumber(messagesBlock.total)}
										accent={ACCENT.blue.kpi}
									/>
									<KpiCard
										icon={UsersIcon}
										label="Mensagens do cliente"
										value={formatNumber(messagesBlock.fromCustomer)}
										hint={formatPercent(
											ratio(messagesBlock.fromCustomer, messagesBlock.total)
										)}
									/>
									<KpiCard
										icon={Radio}
										label="Mensagens de atendente"
										value={formatNumber(messagesBlock.fromMe)}
										hint={formatPercent(
											ratio(messagesBlock.fromMe, messagesBlock.total)
										)}
										accent={ACCENT.emerald.kpi}
									/>
									<KpiCard
										icon={ShieldAlert}
										label="Internas / observações"
										value={formatNumber(messagesBlock.internal)}
										accent={ACCENT.violet.kpi}
									/>
								</div>

								<div className="grid gap-4 xl:grid-cols-2">
									<Panel
										title="Tipos de mídia"
										description="Distribuição por tipo de mensagem no período">
										{(() => {
											const rows = [
												{
													k: "Texto",
													key: "text",
													val: messagesBlock.byType?.text ?? 0,
													color: colors.created,
												},
												{
													k: "Imagem",
													key: "image",
													val: messagesBlock.byType?.image ?? 0,
													color: "#8b5cf6",
												},
												{
													k: "Áudio",
													key: "audio",
													val: messagesBlock.byType?.audio ?? 0,
													color: "#06b6d4",
												},
												{
													k: "Vídeo",
													key: "video",
													val: messagesBlock.byType?.video ?? 0,
													color: "#ef4444",
												},
												{
													k: "Documento",
													key: "document",
													val: messagesBlock.byType?.document ?? 0,
													color: colors.closed,
												},
												{
													k: "Outro",
													key: "other",
													val: messagesBlock.byType?.other ?? 0,
													color: "#64748b",
												},
											];
											const mx = rows.reduce((m, r) => Math.max(m, r.val), 0);
											return rows.some((r) => r.val > 0) ? (
												<div className="h-[280px] w-full">
													<ResponsiveContainer width="100%" height="100%">
														<BarChart
															data={rows}
															margin={{
																top: 8,
																right: 24,
																left: -8,
																bottom: 0,
															}}>
															<CartesianGrid
																strokeDasharray="3 3"
																stroke={colors.grid}
																horizontal={false}
															/>
															<XAxis
																type="number"
																allowDecimals={false}
																tick={{ fontSize: 11, fill: colors.axis }}
																tickLine={false}
																axisLine={false}
															/>
															<YAxis
																type="category"
																dataKey="k"
																width={90}
																tick={{ fontSize: 12, fill: colors.axis }}
																tickLine={false}
																axisLine={false}
															/>
															<ReTooltip
																cursor={{ fillOpacity: 0.08 }}
																contentStyle={chartTooltipStyle}
															/>
															<Bar
																dataKey="val"
																name="Mensagens"
																radius={[0, 6, 6, 0]}
																maxBarSize={22}>
																{rows.map((r) => (
																	<Cell key={r.key} fill={r.color} />
																))}
															</Bar>
														</BarChart>
													</ResponsiveContainer>
												</div>
											) : (
												<EmptyState>Sem mensagens no período.</EmptyState>
											);
										})()}
									</Panel>

									<Panel
										title="Por conexão WhatsApp"
										description="Atendente vs cliente por número/conectado">
										{(() => {
											const rows = Array.isArray(messagesBlock.byWhatsapp)
												? messagesBlock.byWhatsapp.map((w) => ({
														name: w.name || `#${w.whatsappId}`,
														Atendente: w.fromMe ?? 0,
														Cliente: w.fromCustomer ?? 0,
														Interno: w.internal ?? 0,
													}))
												: [];
											return rows.length ? (
												<div className="h-[280px] w-full">
													<ResponsiveContainer width="100%" height="100%">
														<BarChart
															data={rows}
															margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
															<CartesianGrid
																strokeDasharray="3 3"
																stroke={colors.grid}
																vertical={false}
															/>
															<XAxis
																dataKey="name"
																tick={{ fontSize: 11, fill: colors.axis }}
																tickLine={false}
																axisLine={false}
																interval={0}
																angle={-20}
																textAnchor="end"
																height={60}
															/>
															<YAxis
																allowDecimals={false}
																tick={{ fontSize: 11, fill: colors.axis }}
																tickLine={false}
																axisLine={false}
															/>
															<ReTooltip
																cursor={{ fillOpacity: 0.08 }}
																contentStyle={chartTooltipStyle}
															/>
															<Legend wrapperStyle={{ fontSize: 12 }} />
															<Bar
																dataKey="Atendente"
																stackId="wpp"
																fill={colors.closed}
																radius={[4, 4, 0, 0]}
																maxBarSize={30}
															/>
															<Bar
																dataKey="Cliente"
																stackId="wpp"
																fill={colors.created}
																radius={[4, 4, 0, 0]}
																maxBarSize={30}
															/>
															<Bar
																dataKey="Interno"
																stackId="wpp"
																fill="#8b5cf6"
																radius={[4, 4, 0, 0]}
																maxBarSize={30}
															/>
														</BarChart>
													</ResponsiveContainer>
												</div>
											) : (
												<EmptyState>Sem dados de conexão.</EmptyState>
											);
										})()}
									</Panel>
								</div>

								<Panel
									title="Top Respostas Rápidas / Templates"
									description="Os atalhos mais usados no período.">
									{(() => {
										const arr = Array.isArray(messagesBlock.topTemplates)
											? messagesBlock.topTemplates
											: [];
										const rows = arr.map((t) => ({
											name: t.name || t.shortcut || DASH,
											shortcut: t.shortcut || DASH,
											count: t.count ?? 0,
										}));
										const mx = rows.reduce(
											(m, r) => Math.max(m, r.count),
											0
										);
										return rows.length ? (
											<div className="h-[280px] w-full">
												<ResponsiveContainer width="100%" height="100%">
													<BarChart
														data={rows}
														layout="vertical"
														margin={{
															top: 8,
															right: 24,
															left: 8,
															bottom: 0,
														}}>
														<CartesianGrid
															strokeDasharray="3 3"
															stroke={colors.grid}
															horizontal={false}
														/>
														<XAxis
															type="number"
															allowDecimals={false}
															tick={{ fontSize: 11, fill: colors.axis }}
															tickLine={false}
															axisLine={false}
														/>
														<YAxis
															type="category"
															dataKey="name"
															width={180}
															tick={{ fontSize: 11, fill: colors.axis }}
															tickLine={false}
															axisLine={false}
														/>
														<ReTooltip
															cursor={{ fillOpacity: 0.08 }}
															contentStyle={chartTooltipStyle}
														/>
														<Bar
															dataKey="count"
															name="Usos"
															fill={colors.created}
															radius={[0, 6, 6, 0]}
															maxBarSize={16}
														/>
													</BarChart>
												</ResponsiveContainer>
											</div>
										) : (
											<EmptyState>
												Sem uso de respostas rápidas no período.
											</EmptyState>
										);
									})()}
								</Panel>
							</>
						)}
					</TabsContent>

					<TabsContent value="agents" className="mt-4 space-y-4">
						{loading ? (
							renderChartSkeleton()
						) : (
							<>
								<Panel title="Comparativo de atendimento" description="Total de tickets por atendente">
									{userChartData.length ? (
										<div className="h-[280px] w-full">
											<ResponsiveContainer width="100%" height="100%">
												<BarChart
													data={userChartData}
													layout="vertical"
													margin={{ top: 8, right: 24, left: 8, bottom: 0 }}
												>
													<CartesianGrid strokeDasharray="3 3" stroke={colors.grid} horizontal={false} />
													<XAxis
														type="number"
														allowDecimals={false}
														tick={{ fontSize: 11, fill: colors.axis }}
														tickLine={false}
														axisLine={false}
													/>
													<YAxis
														type="category"
														dataKey="name"
														width={140}
														tick={{ fontSize: 11, fill: colors.axis }}
														tickLine={false}
														axisLine={false}
													/>
													<ReTooltip cursor={{ fillOpacity: 0.08 }} contentStyle={chartTooltipStyle} />
													<Bar
														dataKey="total"
														name="Tickets"
														fill={colors.created}
														radius={[0, 4, 4, 0]}
														maxBarSize={18}
													/>
												</BarChart>
											</ResponsiveContainer>
										</div>
									) : (
										<EmptyState>Nenhum atendente com tickets no período.</EmptyState>
									)}
								</Panel>

								<div className="rounded-xl border bg-card overflow-auto">
									<Table>
										<TableHeader className="sticky top-0 z-10 bg-card">
											<TableRow>
												<TableHead>Atendente</TableHead>
												<TableHead className="text-right">Total</TableHead>
												<TableHead className="text-right">Fechados</TableHead>
												<TableHead className="text-right">Taxa</TableHead>
												<TableHead className="text-right">1ª resp (med)</TableHead>
												<TableHead className="text-right">Resol. (med)</TableHead>
												<TableHead className="text-right">SLA 1ª</TableHead>
												<TableHead className="text-right">SLA Resol</TableHead>
												<TableHead className="text-right">Transf. In/Out</TableHead>
												<TableHead className="text-right">Msgs enviadas</TableHead>
												<TableHead className="text-right">Templates</TableHead>
												<TableHead className="text-center">Heatmap (0-23h)</TableHead>
												<TableHead className="text-right">Rating</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{sortedUsers.map((item) => (
												<TableRow key={item.userId ?? item.name}>
													<TableCell className="font-medium whitespace-nowrap">
														{item.name}
													</TableCell>
													<TableCell className="text-right tabular-nums">{formatNumber(item.total)}</TableCell>
													<TableCell className="text-right tabular-nums text-muted-foreground">
														{formatNumber(item.closed)}
													</TableCell>
													<TableCell className="text-right tabular-nums text-muted-foreground">
														{formatPercent(ratio(item.closed, item.total))}
													</TableCell>
													<TableCell className="text-right tabular-nums">
														{formatMinutes(item.avgFirstResponseMinutes)}
													</TableCell>
													<TableCell className="text-right tabular-nums">
														{formatMinutes(item.avgResolutionMinutes)}
													</TableCell>
													<TableCell className="text-right tabular-nums">
														<span className={ACCENT[corStatus(item.slaFirstResponsePct)].kpi}>
															{formatPercent(item.slaFirstResponsePct)}
														</span>
													</TableCell>
													<TableCell className="text-right tabular-nums">
														<span className={ACCENT[corStatus(item.slaResolutionPct)].kpi}>
															{formatPercent(item.slaResolutionPct)}
														</span>
													</TableCell>
													<TableCell className="text-right tabular-nums text-muted-foreground">
														{formatNumber(item.transfersIn)}/
														{formatNumber(item.transfersOut)}
													</TableCell>
													<TableCell className="text-right tabular-nums">
														{formatNumber(item.messagesFromMe)}
													</TableCell>
													<TableCell className="text-right tabular-nums text-muted-foreground">
														{formatNumber(item.templatesUsedCount)}
													</TableCell>
													<TableCell>
														{isHeatmap(item) && (
															<div className="flex items-end gap-[2px] justify-center">
																{buildByHour(item).map((x) => (
																	<div
																		key={x.hour}
																		title={`${x.hour}h: ${x.total} ticket(s)`}
																		style={{
																			backgroundColor: heatColor(x.total, 0, item.heatmax || 1),
																			width: 3,
																			height: Math.max(
																				4,
																				Math.min(
																					20,
																					4 +
																						((x.total || 0) / Math.max(1, item.heatmax || 1)) *
																							16
																				)
																			),
																		}}
																		className="rounded-[1px] transition-all"
																	/>
																))}
															</div>
														)}
													</TableCell>
													<TableCell className="text-right">
														<div className="flex justify-end">
															<Stars value={item.avgRating} />
														</div>
													</TableCell>
												</TableRow>
											))}
											{!sortedUsers.length && (
												<TableRow>
													<TableCell
														colSpan={13}
														className="py-10 text-center text-sm text-muted-foreground">
														Nenhum atendente com tickets no período.
													</TableCell>
												</TableRow>
											)}
										</TableBody>
									</Table>
								</div>
							</>
						)}
					</TabsContent>

					{/* Filas & Etiquetas */}
					<TabsContent value="queues" className="mt-4 space-y-4">
						{loading ? (
							renderChartSkeleton()
						) : (
							<>
								<div className="grid gap-4 xl:grid-cols-2">
									<Panel title="Distribuição por fila" description="Participação de cada fila no total">
										{sortedQueues.length ? (
											<div className="h-[280px] w-full">
												<ResponsiveContainer width="100%" height="100%">
													<PieChart>
														<Pie
															data={sortedQueues}
															dataKey="total"
															nameKey="name"
															innerRadius={60}
															outerRadius={95}
															paddingAngle={2}
															stroke="none"
														>
															{sortedQueues.map((item, index) => (
																<Cell
																	key={item.queueId ?? `queue-${index}`}
																	fill={item.color || colors.categorical[index % colors.categorical.length]}
																/>
															))}
														</Pie>
														<ReTooltip contentStyle={chartTooltipStyle} />
														<Legend wrapperStyle={{ fontSize: 12 }} />
													</PieChart>
												</ResponsiveContainer>
											</div>
										) : (
											<EmptyState>Nenhuma fila com tickets no período.</EmptyState>
										)}
									</Panel>

									<Panel title="Tickets por fila" description="Volume, tempos médios e SLA por fila">
										{sortedQueues.length ? (
											<div className="max-h-[340px] overflow-auto">
												<Table>
													<TableHeader className="sticky top-0 z-10 bg-card">
														<TableRow>
															<TableHead>Fila</TableHead>
															<TableHead className="text-right">Total</TableHead>
															<TableHead className="text-right">Em and.</TableHead>
															<TableHead className="text-right">Aguar.</TableHead>
															<TableHead className="text-right">Fechados</TableHead>
															<TableHead className="text-right">
																Espera média (min)
															</TableHead>
															<TableHead className="text-right">
																Resol. média (min)
															</TableHead>
															<TableHead className="text-right">SLA 1ª</TableHead>
															<TableHead className="text-right">SLA Resol</TableHead>
														</TableRow>
													</TableHeader>
													<TableBody>
														{sortedQueues.map((item, index) => (
															<TableRow key={item.queueId ?? `queue-row-${index}`}>
																<TableCell>
																	<div className="flex items-center gap-2">
																		<span
																			className="h-3 w-3 shrink-0 rounded-full border"
																			style={{
																				backgroundColor:
																					item.color ||
																					colors.categorical[
																						index % colors.categorical.length
																					],
																			}}
																		/>
																		<span className="font-medium">
																			{item.name}
																		</span>
																	</div>
																</TableCell>
																<TableCell className="text-right tabular-nums">
																	{formatNumber(item.total)}
																</TableCell>
																<TableCell className="text-right tabular-nums text-muted-foreground">
																	{formatNumber(item.open)}
																</TableCell>
																<TableCell className="text-right tabular-nums text-amber-600">
																	{formatNumber(item.pending)}
																</TableCell>
																<TableCell className="text-right tabular-nums text-emerald-600">
																	{formatNumber(item.closed)}
																</TableCell>
																<TableCell className="text-right tabular-nums">
																	{formatMinutes(item.avgWaitMinutes)}
																</TableCell>
																<TableCell className="text-right tabular-nums">
																	{formatMinutes(item.avgResolutionMinutes)}
																</TableCell>
																<TableCell className="text-right tabular-nums">
																	<span
																		className={
																			ACCENT[
																				corStatus(
																					item.slaFirstResponsePct
																				)
																			].kpi
																		}>
																		{formatPercent(
																			item.slaFirstResponsePct
																		)}
																	</span>
																</TableCell>
																<TableCell className="text-right tabular-nums">
																	<span
																		className={
																			ACCENT[
																				corStatus(item.slaResolutionPct)
																			].kpi
																		}>
																		{formatPercent(
																			item.slaResolutionPct
																		)}
																	</span>
																</TableCell>
															</TableRow>
														))}
													</TableBody>
												</Table>
											</div>
										) : (
											<EmptyState>Nenhuma fila com tickets no período.</EmptyState>
										)}
									</Panel>
								</div>

								<Panel
									title="Etiquetas mais usadas"
									description="Uso geral, taxa de resolução e tempo médio por etiqueta.">
									{sortedTags.length ? (
										<>
											<div className="mb-4 flex flex-wrap gap-2">
												{sortedTags.map((tag, index) => (
													<Badge
														key={tag.tagId ?? `tag-${index}`}
														className="border-transparent text-white"
														style={{
															backgroundColor:
																tag.color || colors.categorical[index % colors.categorical.length],
														}}>
														{tag.name} · {formatNumber(tag.total)}
													</Badge>
												))}
											</div>
											<div className="grid gap-4 xl:grid-cols-2">
												<div className="h-[280px] w-full">
													<ResponsiveContainer width="100%" height="100%">
														<BarChart
															data={sortedTags.slice(0, 12)}
															margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
															<CartesianGrid strokeDasharray="3 3" stroke={colors.grid} vertical={false} />
															<XAxis
																dataKey="name"
																tick={{ fontSize: 11, fill: colors.axis }}
																tickLine={false}
																axisLine={false}
																interval={0}
																height={50}
																angle={-20}
																textAnchor="end"
															/>
															<YAxis
																allowDecimals={false}
																tick={{ fontSize: 11, fill: colors.axis }}
																tickLine={false}
																axisLine={false}
															/>
															<ReTooltip cursor={{ fillOpacity: 0.08 }} contentStyle={chartTooltipStyle} />
															<Bar dataKey="total" name="Tickets" radius={[4, 4, 0, 0]} maxBarSize={40}>
																{sortedTags.slice(0, 12).map((tag, index) => (
																	<Cell
																		key={tag.tagId ?? `tag-bar-${index}`}
																		fill={tag.color || colors.categorical[index % colors.categorical.length]}
																	/>
																))}
															</Bar>
														</BarChart>
													</ResponsiveContainer>
												</div>
												<div className="max-h-[280px] overflow-auto">
													<Table>
														<TableHeader className="sticky top-0 z-10 bg-card">
															<TableRow>
																<TableHead>Etiqueta</TableHead>
																<TableHead className="text-right">Total</TableHead>
																<TableHead className="text-right">Fechados</TableHead>
																<TableHead className="text-right">Taxa resolução</TableHead>
																<TableHead className="text-right">
																	Resol. média (min)
																</TableHead>
															</TableRow>
														</TableHeader>
														<TableBody>
															{sortedTags.map((tag, index) => (
																<TableRow key={tag.tagId ?? `tag-row-${index}`}>
																	<TableCell>
																		<div className="flex items-center gap-2">
																			<span
																				className="h-3 w-3 shrink-0 rounded-full border"
																				style={{
																					backgroundColor:
																						tag.color ||
																						colors.categorical[
																							index % colors.categorical.length
																						],
																				}}
																			/>
																			<span className="font-medium">{tag.name}</span>
																		</div>
																	</TableCell>
																	<TableCell className="text-right tabular-nums">
																		{formatNumber(tag.total)}
																	</TableCell>
																	<TableCell className="text-right tabular-nums text-muted-foreground">
																		{formatNumber(tag.closedCount)}
																	</TableCell>
																	<TableCell className="text-right tabular-nums">
																		<span
																			className={
																				ACCENT[corStatus(tag.resolutionRate, { goodAbove: 70, warnAbove: 45 })].kpi
																			}>
																			{formatPercent(tag.resolutionRate)}
																		</span>
																	</TableCell>
																	<TableCell className="text-right tabular-nums">
																		{formatMinutes(tag.avgResolutionMinutes)}
																	</TableCell>
																</TableRow>
															))}
														</TableBody>
													</Table>
												</div>
											</div>
										</>
									) : (
										<EmptyState>Nenhuma etiqueta aplicada no período.</EmptyState>
									)}
								</Panel>
							</>
						)}
					</TabsContent>

					{/* Fila & Triagem */}
					<TabsContent value="triage" className="mt-4 space-y-4">
						{loading ? (
							renderChartSkeleton()
						) : (
							<>
								<div className="grid gap-4 xl:grid-cols-2">
									<Panel
										title="Transferências por hora"
										description="Volume de transferências por faixa horária.">
										{(() => {
											const arr = Array.isArray(transfersBlock?.byHour)
												? transfersBlock.byHour
												: [];
											const rows = new Array(24).fill(0).map((_, hour) => {
												const match = arr.find((h) => h.hour === hour);
												return {
													label: `${String(hour).padStart(2, "0")}h`,
													total: match?.total ?? 0,
												};
											});
											return rows.some((r) => r.total > 0) ? (
												<div className="h-[280px] w-full">
													<ResponsiveContainer width="100%" height="100%">
														<BarChart
															data={rows}
															margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
															<CartesianGrid
																strokeDasharray="3 3"
																stroke={colors.grid}
																vertical={false}
															/>
															<XAxis
																dataKey="label"
																tick={{ fontSize: 11, fill: colors.axis }}
																tickLine={false}
																axisLine={false}
																interval={1}
															/>
															<YAxis
																allowDecimals={false}
																tick={{ fontSize: 11, fill: colors.axis }}
																tickLine={false}
																axisLine={false}
															/>
															<ReTooltip
																cursor={{ fillOpacity: 0.08 }}
																contentStyle={chartTooltipStyle}
															/>
															<Bar
																dataKey="total"
																name="Transferências"
																fill={colors.created}
																radius={[4, 4, 0, 0]}
																maxBarSize={22}
															/>
														</BarChart>
													</ResponsiveContainer>
												</div>
											) : (
												<EmptyState>
													Sem dados de transferências no período.
												</EmptyState>
											);
										})()}
									</Panel>

									<Panel
										title="Transferências por dia da semana"
										description="Demanda acumulada por dia.">
										{(() => {
											const arr = Array.isArray(transfersBlock?.byWeekday)
												? transfersBlock.byWeekday
												: [];
											const base = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
											const rows = base.map((label, weekday) => {
												const m = arr.find(
													(x) =>
														(x.weekday === weekday ||
															x.label === label)
												);
												return { label, total: m?.total ?? 0 };
											});
											return rows.some((r) => r.total > 0) ? (
												<div className="h-[280px] w-full">
													<ResponsiveContainer width="100%" height="100%">
														<BarChart
															data={rows}
															margin={{ top: 8, right: 24, left: 8, bottom: 0 }}>
															<CartesianGrid
																strokeDasharray="3 3"
																stroke={colors.grid}
																horizontal={false}
															/>
															<XAxis
																type="number"
																allowDecimals={false}
																tick={{ fontSize: 11, fill: colors.axis }}
																tickLine={false}
																axisLine={false}
															/>
															<YAxis
																type="category"
																dataKey="label"
																width={48}
																tick={{ fontSize: 12, fill: colors.axis }}
																tickLine={false}
																axisLine={false}
															/>
															<ReTooltip
																cursor={{ fillOpacity: 0.08 }}
																contentStyle={chartTooltipStyle}
															/>
															<Bar
																dataKey="total"
																name="Transferências"
																fill={colors.closed}
																radius={[0, 6, 6, 0]}
																maxBarSize={20}
															/>
														</BarChart>
													</ResponsiveContainer>
												</div>
											) : (
												<EmptyState>Sem transferências no período.</EmptyState>
											);
										})()}
									</Panel>
								</div>

								<Panel
									title="Matriz de transferências (de → para)"
									description="50 combinações mais frequentes entre filas.">
									{(() => {
										const matrix = Array.isArray(transfersBlock?.matrix)
											? transfersBlock.matrix
											: [];
										const mx = matrix.reduce(
											(m, r) => Math.max(m, r.count || 0),
											0
										);
										return matrix.length ? (
											<div className="max-h-[340px] overflow-auto">
												<Table>
													<TableHeader className="sticky top-0 z-10 bg-card">
														<TableRow>
															<TableHead>De fila</TableHead>
															<TableHead>Para fila</TableHead>
															<TableHead className="text-right">Qtd</TableHead>
															<TableHead className="w-1/2">Intensidade</TableHead>
														</TableRow>
													</TableHeader>
													<TableBody>
														{matrix.map((row, i) => (
															<TableRow
																key={`${row.fromQueue}-${row.toQueue}-${i}`}>
																<TableCell className="font-medium">
																	{row.fromQueue || "(sem fila)"}
																</TableCell>
																<TableCell>
																	{row.toQueue || "(sem fila)"}
																</TableCell>
																<TableCell className="text-right tabular-nums">
																	{formatNumber(row.count)}
																</TableCell>
																<TableCell>
																	<div
																		className="h-2 rounded-full bg-slate-200 dark:bg-slate-700/60"
																		style={{
																			width: `${Math.min(
																				100,
																				ratio(row.count, mx) ?? 0
																			)}%`,
																			backgroundColor: heatColor(
																				row.count,
																				0,
																				mx || 1
																			),
																		}}
																	/>
																</TableCell>
															</TableRow>
														))}
													</TableBody>
												</Table>
											</div>
										) : (
											<EmptyState>
												Nenhuma transferência registrada no período.
											</EmptyState>
										);
									})()}
								</Panel>

								<Panel
									title="Triagem por dia da semana"
									description="Dias com maior volume, gargalos e SLA. Clique nas linhas para priorizar ações.">
									{triageByWeekday.some(
										(d) => (d.total || 0) > 0
									) ? (
										<div className="overflow-auto">
											<Table>
												<TableHeader className="sticky top-0 z-10 bg-card">
													<TableRow>
														<TableHead>Dia</TableHead>
														<TableHead className="text-right">Total</TableHead>
														<TableHead className="text-right">Fechados</TableHead>
														<TableHead className="text-right">SLA resolução</TableHead>
														<TableHead className="text-right">
															1ª resposta (med)
														</TableHead>
														<TableHead className="text-right">
															Resolução (med)
														</TableHead>
													</TableRow>
												</TableHeader>
												<TableBody>
													{triageByWeekday.map((d) => (
														<TableRow key={d.weekday}>
															<TableCell className="font-medium">{d.label}</TableCell>
															<TableCell className="text-right tabular-nums">
																{formatNumber(d.total)}
															</TableCell>
															<TableCell className="text-right tabular-nums text-muted-foreground">
																{formatNumber(d.closed)}
															</TableCell>
															<TableCell className="text-right tabular-nums">
																<span
																	className={
																		ACCENT[corStatus(d.slaResolutionPct, { goodAbove: 75, warnAbove: 50 })].kpi
																	}>
																	{formatPercent(d.slaResolutionPct)}
																</span>
															</TableCell>
															<TableCell className="text-right tabular-nums">
																{formatMinutes(d.avgFirstResponseMinutes)}
															</TableCell>
															<TableCell className="text-right tabular-nums">
																{formatMinutes(d.avgResolutionMinutes)}
															</TableCell>
														</TableRow>
													))}
												</TableBody>
											</Table>
										</div>
									) : (
										<EmptyState>Sem dados de triagem no período.</EmptyState>
									)}
								</Panel>
							</>
						)}
					</TabsContent>

					{/* Satisfação */}
					<TabsContent value="satisfaction" className="mt-4 space-y-4">
						{loading ? (
							renderChartSkeleton()
						) : (
							<>
								<div className="grid gap-4 xl:grid-cols-2">
									<Panel
										title="Avaliações (CSAT)"
										description={`${formatNumber(ratings.total)} avaliação(ões) no período`}
										action={
											<div className="text-right">
												<p className="text-2xl font-bold leading-none text-foreground">
													{isNum(ratings.average) ? formatDecimal(ratings.average, 1) : DASH}
												</p>
												<p className="mt-1 text-xs text-muted-foreground">Média geral</p>
											</div>
										}
									>
										{hasRatings ? (
											<div className="h-[280px] w-full">
												<ResponsiveContainer width="100%" height="100%">
													<BarChart data={ratingData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
														<CartesianGrid strokeDasharray="3 3" stroke={colors.grid} vertical={false} />
														<XAxis
															dataKey="label"
															tick={{ fontSize: 12, fill: colors.axis }}
															tickLine={false}
															axisLine={false}
														/>
														<YAxis
															allowDecimals={false}
															tick={{ fontSize: 11, fill: colors.axis }}
															tickLine={false}
															axisLine={false}
														/>
														<ReTooltip cursor={{ fillOpacity: 0.08 }} contentStyle={chartTooltipStyle} />
														<Bar dataKey="total" name="Avaliações" radius={[4, 4, 0, 0]} maxBarSize={48}>
															{ratingData.map((item) => (
																<Cell
																	key={item.rate}
																	fill={colors.rating[Math.min(Math.max(item.rate, 1), 5) - 1]}
																/>
															))}
														</Bar>
													</BarChart>
												</ResponsiveContainer>
											</div>
										) : (
											<EmptyState>Sem dados de avaliação no período.</EmptyState>
										)}
									</Panel>

									<Panel
										title="NPS"
										description={`${formatNumber(nps.total)} resposta(s) considerada(s)`}
										action={
											<div className="text-right">
												<p className="text-2xl font-bold leading-none text-foreground">
													{isNum(nps.score) ? formatDecimal(nps.score, 0) : DASH}
												</p>
												<p className="mt-1 text-xs text-muted-foreground">
													{isNum(nps.score) ? "Score NPS" : "Sem dados"}
												</p>
											</div>
										}
									>
										{nps.total > 0 ? (
											<div className="h-[280px] w-full">
												<ResponsiveContainer width="100%" height="100%">
													<BarChart
														data={npsData}
														layout="vertical"
														margin={{ top: 8, right: 24, left: 8, bottom: 0 }}
													>
														<CartesianGrid strokeDasharray="3 3" stroke={colors.grid} horizontal={false} />
														<XAxis
															type="number"
															allowDecimals={false}
															tick={{ fontSize: 11, fill: colors.axis }}
															tickLine={false}
															axisLine={false}
														/>
														<YAxis
															type="category"
															dataKey="name"
															width={100}
															tick={{ fontSize: 12, fill: colors.axis }}
															tickLine={false}
															axisLine={false}
														/>
														<ReTooltip cursor={{ fillOpacity: 0.08 }} contentStyle={chartTooltipStyle} />
														<Bar dataKey="total" name="Respostas" radius={[0, 4, 4, 0]} maxBarSize={28}>
															{npsData.map((item) => (
																<Cell key={item.name} fill={item.color} />
															))}
														</Bar>
													</BarChart>
												</ResponsiveContainer>
											</div>
										) : (
											<EmptyState>Sem dados de NPS no período.</EmptyState>
										)}
									</Panel>
								</div>

								<div className="grid gap-3 sm:grid-cols-3">
									{npsData.map((item) => (
										<div key={item.name} className="rounded-xl border bg-card p-4">
											<div className="flex items-center gap-2">
												<span
													className="h-3 w-3 rounded-full"
													style={{ backgroundColor: item.color }}
												/>
												<span className="text-xs font-medium text-muted-foreground">{item.name}</span>
											</div>
											<p className="mt-2 text-2xl font-bold tracking-tight text-foreground">
												{formatNumber(item.total)}
											</p>
											<p className="mt-1 text-xs text-muted-foreground">
												{formatPercent(ratio(item.total, nps.total))}
											</p>
										</div>
									))}
								</div>
							</>
						)}
					</TabsContent>

					{/* IA & Acompanhamento */}
					<TabsContent value="ai" className="mt-4 space-y-4">
						{(() => {
							const handleRunAi = async () => {
								setLoadingAi(true);
								try {
									const params = buildParams(startDate, endDate);
									const res = await api.post("/reports/tickets/ai-analysis", params);
									setAi(res.data || null);
									toast.success("Análise concluída!");
								} catch (err) {
									toastError(err);
								} finally {
									setLoadingAi(false);
								}
							};

							const levelMeta = {
								bom: {
									label: "Bom desempenho",
									badge: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-400/30",
									border: "border-emerald-400/30 bg-emerald-50/40 dark:bg-emerald-950/30",
									icon: ThumbsUp,
								},
								atencao: {
									label: "Precisa de atenção",
									badge: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-400/30",
									border: "border-amber-400/30 bg-amber-50/40 dark:bg-amber-950/30",
									icon: ShieldAlert,
								},
								critico: {
									label: "Crítico — intervenção",
									badge: "bg-red-500/15 text-red-700 dark:text-red-300 border-red-400/30",
									border: "border-red-400/30 bg-red-50/40 dark:bg-red-950/30",
									icon: AlertCircle,
								},
							};

							return (
								<>
									<Panel
										title={
											<span className="inline-flex items-center gap-2">
												<Sparkles className="h-5 w-5 text-violet-500" />
												Análise inteligente do atendimento
											</span>
										}
										description={
											ai?.mode === "heuristic"
												? "Modelo heurístico — avalia SLA, reaberturas, transferências, tempo de resolução e volume por atendente. Para análise com IA generativa, configure uma chave de LLM no backend."
												: "Análise gerada por IA a partir dos KPIs do período."
										}
										action={
											<Button
												size="sm"
												onClick={handleRunAi}
												disabled={loading || loadingAi}
												className="inline-flex items-center gap-2">
												<Sparkles className="h-4 w-4" />
												{loadingAi ? "Analisando…" : ai ? "Atualizar análise" : "Gerar análise IA"}
											</Button>
										}
									>
										{loadingAi && !ai ? (
											<div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
												{Array.from({ length: 6 }).map((_, i) => (
													<Skeleton key={i} className="h-40 w-full rounded-xl" />
												))}
											</div>
										) : !ai ? (
											<EmptyState>
												Clique em <strong>Gerar análise IA</strong> para avaliar o desempenho de cada
												atendente e identificar quem precisa de atenção, treinamento ou realocação.
											</EmptyState>
										) : (
											<>
												{Array.isArray(ai.summary) && ai.summary.length ? (
													<div
														className={cn(
															"mb-6 rounded-xl border p-4 text-sm leading-relaxed",
															ai.flags && ai.flags.length
																? "border-amber-300/60 bg-amber-50/60 dark:bg-amber-950/20 text-amber-900 dark:text-amber-100"
																: "border-slate-200 bg-slate-50 text-slate-800 dark:bg-slate-900/60 dark:text-slate-100 dark:border-slate-800"
														)}>
														<div className="mb-2 flex items-center gap-2 font-semibold">
															{ai.flags && ai.flags.length ? (
																<>
																	<ShieldAlert className="h-4 w-4 text-amber-600 dark:text-amber-400" />
																	Visão geral do período
																</>
															) : (
																<>
																	<ThumbsUp className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
																	Visão geral do período
																</>
															)}
														</div>
														<ul className="list-disc space-y-1 pl-5 marker:text-slate-400">
															{ai.summary.map((s, i) => (
																<li key={i}>{s}</li>
															))}
														</ul>
														{Array.isArray(ai.flags) && ai.flags.length ? (
															<ul className="mt-3 list-disc space-y-1 pl-5 text-amber-800 dark:text-amber-200 marker:text-amber-500">
																{ai.flags.map((f, i) => (
																	<li key={i}>{f}</li>
																))}
															</ul>
														) : null}
													</div>
												) : null}

												{Array.isArray(ai.needsAttention) && ai.needsAttention.length ? (
													<div className="mb-6">
														<div className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">
															<ThumbsDown className="h-4 w-4 text-red-500" />
															Prioridade — {ai.needsAttention.length} atendente(s) precisam de atenção
														</div>
														<div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
															{ai.needsAttention.map((c) => {
																const meta = levelMeta[c.level] || levelMeta.atencao;
																const Icon = meta.icon;
																const vol = c.volume || {};
																const met = c.metrics || {};
																return (
																	<div
																		key={`need-${c.userId}`}
																		className={cn(
																			"rounded-xl border p-4 shadow-sm space-y-4",
																			meta.border
																		)}>
																		<div className="flex items-start justify-between gap-3">
																			<div className="min-w-0 flex-1">
																				<div className="flex items-center gap-2">
																					<span className={cn(
																						"inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold",
																						c.level === "critico"
																							? "bg-red-500 text-white"
																							: "bg-amber-500 text-white"
																					)}>
																						#{typeof c.rank === "number" ? c.rank : "·"}
																					</span>
																					<div className="min-w-0">
																						<div className="truncate text-base font-semibold text-foreground">
																							{c.name}
																						</div>
																						{c.email ? (
																							<div className="truncate text-xs text-muted-foreground">
																								{c.email}
																							</div>
																						) : null}
																					</div>
																				</div>
																			</div>
																			<Badge
																				className={cn(
																					"shrink-0 whitespace-nowrap border",
																					meta.badge
																				)}>
																				<Icon className="mr-1 h-3 w-3 inline-block align-sub" />
																				{meta.label} · {c.score}
																			</Badge>
																		</div>

																		<div className="grid grid-cols-4 gap-2 text-center">
																			<div className="rounded-lg bg-slate-50/70 dark:bg-slate-900/40 border border-slate-200/60 dark:border-slate-800/60 p-2">
																				<div className="text-base font-bold text-slate-900 dark:text-slate-50 tabular-nums">{formatNumber(vol.totalTickets)}</div>
																				<div className="text-[10px] uppercase tracking-wide text-slate-500">Atendim.</div>
																			</div>
																			<div className="rounded-lg bg-emerald-50/70 dark:bg-emerald-950/30 border border-emerald-200/60 dark:border-emerald-900/40 p-2">
																				<div className="text-base font-bold text-emerald-700 dark:text-emerald-300 tabular-nums">{formatNumber(vol.closedCount)}</div>
																				<div className="text-[10px] uppercase tracking-wide text-emerald-600/80 dark:text-emerald-400/80">Fechados</div>
																			</div>
																			<div className="rounded-lg bg-sky-50/70 dark:bg-sky-950/30 border border-sky-200/60 dark:border-sky-900/40 p-2">
																				<div className="text-base font-bold text-sky-700 dark:text-sky-300 tabular-nums">{formatNumber(vol.openCount)}</div>
																				<div className="text-[10px] uppercase tracking-wide text-sky-600/80 dark:text-sky-400/80">Em atend.</div>
																			</div>
																			<div className="rounded-lg bg-amber-50/70 dark:bg-amber-950/30 border border-amber-200/60 dark:border-amber-900/40 p-2">
																				<div className="text-base font-bold text-amber-700 dark:text-amber-300 tabular-nums">{formatNumber(vol.pendingCount)}</div>
																				<div className="text-[10px] uppercase tracking-wide text-amber-600/80 dark:text-amber-400/80">Aguard.</div>
																			</div>
																		</div>

																		<div className="grid grid-cols-4 gap-2 text-center">
																			<div className="rounded-md border border-red-200/60 dark:border-red-900/40 bg-red-50/70 dark:bg-red-950/30 p-1.5">
																				<div className="text-sm font-bold text-red-700 dark:text-red-300 tabular-nums">{formatNumber(vol.reopenCount)}</div>
																				<div className="text-[10px] uppercase tracking-wide text-red-600/80 dark:text-red-400/80">Reabr.</div>
																			</div>
																			<div className="rounded-md border border-slate-200/60 dark:border-slate-800/60 bg-slate-50/70 dark:bg-slate-900/40 p-1.5">
																				<div className="text-sm font-bold text-slate-800 dark:text-slate-100 tabular-nums">{formatNumber(vol.transfersIn)}<span className="text-slate-400 mx-0.5">/</span>{formatNumber(vol.transfersOut)}</div>
																				<div className="text-[10px] uppercase tracking-wide text-slate-500">← / → Transf.</div>
																			</div>
																			<div className="rounded-md border border-violet-200/60 dark:border-violet-900/40 bg-violet-50/70 dark:bg-violet-950/30 p-1.5">
																				<div className="text-sm font-bold text-violet-700 dark:text-violet-300 tabular-nums">{formatNumber(vol.messagesFromMe)}</div>
																				<div className="text-[10px] uppercase tracking-wide text-violet-600/80 dark:text-violet-400/80">Msgs env.</div>
																			</div>
																			<div className="rounded-md border border-indigo-200/60 dark:border-indigo-900/40 bg-indigo-50/70 dark:bg-indigo-950/30 p-1.5">
																				<div className="text-sm font-bold text-indigo-700 dark:text-indigo-300 tabular-nums">{formatNumber(vol.messagesFromCustomer)}</div>
																				<div className="text-[10px] uppercase tracking-wide text-indigo-600/80 dark:text-indigo-400/80">Msgs receb.</div>
																			</div>
																		</div>

																		<div className="space-y-2 pt-1">
																			<div className="space-y-1">
																				<div className="flex items-center justify-between text-[11px]">
																					<span className="text-slate-600 dark:text-slate-400">SLA 1ª resposta</span>
																					<span className={cn("tabular-nums font-semibold", ACCENT[corStatus(met.slaFirstResponsePct)].kpi)}>{formatPercent(met.slaFirstResponsePct)}</span>
																				</div>
																				<Progress value={Math.min(100, met.slaFirstResponsePct || 0)} status={corStatus(met.slaFirstResponsePct)} />
																			</div>
																			<div className="space-y-1">
																				<div className="flex items-center justify-between text-[11px]">
																					<span className="text-slate-600 dark:text-slate-400">SLA resolução</span>
																					<span className={cn("tabular-nums font-semibold", ACCENT[corStatus(met.slaResolutionPct)].kpi)}>{formatPercent(met.slaResolutionPct)}</span>
																				</div>
																				<Progress value={Math.min(100, met.slaResolutionPct || 0)} status={corStatus(met.slaResolutionPct)} />
																			</div>
																			<div className="space-y-1">
																				<div className="flex items-center justify-between text-[11px]">
																					<span className="text-slate-600 dark:text-slate-400">Reaberturas (meta ≤8%)</span>
																					<span className={cn("tabular-nums font-semibold", ACCENT[corStatusInvert(met.reopenPct, { badAbove: 15, warnAbove: 8 })].kpi)}>{formatPercent(met.reopenPct)}</span>
																				</div>
																				<Progress value={Math.min(100, (met.reopenPct || 0) * 4)} status={corStatusInvert(met.reopenPct, { badAbove: 15, warnAbove: 8 })} />
																			</div>
																		</div>

																		<p className="mt-1 text-sm leading-relaxed text-foreground/90">
																			{c.summary}
																		</p>
																		{c.flags?.length ? (
																			<ul className="mt-1 list-disc space-y-1 pl-5 text-xs text-muted-foreground marker:text-slate-500">
																				{c.flags.map((f, i) => (
																					<li key={i}>{f}</li>
																				))}
																			</ul>
																		) : null}
																	</div>
																);
															})}
														</div>
													</div>
												) : null}

												{Array.isArray(ai.topPerformers) && ai.topPerformers.length ? (
													<div className="mb-6">
														<div className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">
															<Trophy className="h-4 w-4 text-amber-500" />
															Destaques — Top {ai.topPerformers.length} desempenho
														</div>
														<div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
															{ai.topPerformers.map((c) => {
																const meta = levelMeta[c.level] || levelMeta.bom;
																const Icon = meta.icon;
																const vol = c.volume || {};
																const met = c.metrics || {};
																return (
																	<div
																		key={`top-${c.userId}`}
																		className={cn(
																			"rounded-xl border p-4 shadow-sm space-y-4",
																			meta.border
																		)}>
																		<div className="flex items-start justify-between gap-3">
																			<div className="min-w-0 flex-1">
																				<div className="flex items-center gap-2">
																					<span className="inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold bg-emerald-500 text-white shadow-sm shadow-emerald-200 dark:shadow-emerald-900/60">
																						#{typeof c.rank === "number" ? c.rank : "·"}
																					</span>
																					<div className="min-w-0">
																						<div className="truncate text-base font-semibold text-foreground">
																							{c.name}
																						</div>
																						{c.email ? (
																							<div className="truncate text-xs text-muted-foreground">
																								{c.email}
																							</div>
																						) : null}
																					</div>
																				</div>
																				{Array.isArray(c.highlights) && c.highlights.length ? (
																					<div className="mt-2 flex flex-wrap gap-1">
																						{c.highlights.slice(0, 3).map((h, i) => (
																							<span key={i} className="inline-flex items-center rounded-full border border-emerald-200/60 bg-emerald-50/70 dark:bg-emerald-950/30 dark:border-emerald-900/50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-300">
																								⭐ {h}
																							</span>
																						))}
																					</div>
																				) : null}
																			</div>
																			<Badge
																				className={cn(
																					"shrink-0 whitespace-nowrap border",
																					meta.badge
																				)}>
																				<Icon className="mr-1 h-3 w-3 inline-block align-sub" />
																				Bom · {c.score}
																			</Badge>
																		</div>

																		<div className="grid grid-cols-4 gap-2 text-center">
																			<div className="rounded-lg bg-slate-50/70 dark:bg-slate-900/40 border border-slate-200/60 dark:border-slate-800/60 p-2">
																				<div className="text-base font-bold text-slate-900 dark:text-slate-50 tabular-nums">{formatNumber(vol.totalTickets)}</div>
																				<div className="text-[10px] uppercase tracking-wide text-slate-500">Atendim.</div>
																			</div>
																			<div className="rounded-lg bg-emerald-50/70 dark:bg-emerald-950/30 border border-emerald-200/60 dark:border-emerald-900/40 p-2">
																				<div className="text-base font-bold text-emerald-700 dark:text-emerald-300 tabular-nums">{formatNumber(vol.closedCount)}</div>
																				<div className="text-[10px] uppercase tracking-wide text-emerald-600/80 dark:text-emerald-400/80">Fechados</div>
																			</div>
																			<div className="rounded-lg bg-rose-50/70 dark:bg-rose-950/20 border border-rose-200/60 dark:border-rose-900/40 p-2">
																				<div className="text-base font-bold text-rose-700 dark:text-rose-300 tabular-nums">{formatNumber(vol.reopenCount)}</div>
																				<div className="text-[10px] uppercase tracking-wide text-rose-600/80 dark:text-rose-400/80">Reabr.</div>
																			</div>
																			<div className="rounded-lg bg-violet-50/70 dark:bg-violet-950/30 border border-violet-200/60 dark:border-violet-900/40 p-2">
																				<div className="text-base font-bold text-violet-700 dark:text-violet-300 tabular-nums">{formatNumber(vol.messagesFromMe)}</div>
																				<div className="text-[10px] uppercase tracking-wide text-violet-600/80 dark:text-violet-400/80">Msgs env.</div>
																			</div>
																		</div>

																		<div className="space-y-2 pt-1">
																			<div className="space-y-1">
																				<div className="flex items-center justify-between text-[11px]">
																					<span className="text-slate-600 dark:text-slate-400">SLA 1ª resposta</span>
																					<span className={cn("tabular-nums font-semibold", ACCENT[corStatus(met.slaFirstResponsePct)].kpi)}>{formatPercent(met.slaFirstResponsePct)}</span>
																				</div>
																				<Progress value={Math.min(100, met.slaFirstResponsePct || 0)} status={corStatus(met.slaFirstResponsePct)} />
																			</div>
																			<div className="space-y-1">
																				<div className="flex items-center justify-between text-[11px]">
																					<span className="text-slate-600 dark:text-slate-400">SLA resolução</span>
																					<span className={cn("tabular-nums font-semibold", ACCENT[corStatus(met.slaResolutionPct)].kpi)}>{formatPercent(met.slaResolutionPct)}</span>
																				</div>
																				<Progress value={Math.min(100, met.slaResolutionPct || 0)} status={corStatus(met.slaResolutionPct)} />
																			</div>
																		</div>

																		<p className="mt-1 text-sm leading-relaxed text-foreground/90">
																			{c.summary}
																		</p>
																	</div>
																);
															})}
														</div>
													</div>
												) : null}

												{Array.isArray(ai.attendants) && ai.attendants.length ? (
													<div className="overflow-auto">
														<Table>
															<TableHeader>
																<TableRow>
																	<TableHead className="w-[60px]">#</TableHead>
																	<TableHead>Atendente</TableHead>
																	<TableHead>Nível</TableHead>
																	<TableHead className="text-right">Score</TableHead>
																	<TableHead className="text-right">Total</TableHead>
																	<TableHead className="text-right">Fechados</TableHead>
																	<TableHead className="text-right">Aberto</TableHead>
																	<TableHead className="text-right">Aguard.</TableHead>
																	<TableHead className="text-right">Reabr.</TableHead>
																	<TableHead className="text-right">SLA 1ª</TableHead>
																	<TableHead className="text-right">SLA Resol</TableHead>
																	<TableHead className="text-right">Reabr %</TableHead>
																	<TableHead className="text-right">Transf</TableHead>
																	<TableHead className="text-right">Msgs</TableHead>
																	<TableHead className="text-right">T. Médio</TableHead>
																</TableRow>
															</TableHeader>
															<TableBody>
																{ai.attendants.map((c) => {
																	const meta = levelMeta[c.level] || levelMeta.atencao;
																	const Icon = meta.icon;
																	const vol = c.volume || {};
																	const met = c.metrics || {};
																	return (
																		<TableRow key={`tbl-${c.userId}`}>
																			<TableCell className="font-medium text-slate-500 tabular-nums">
																				#{typeof c.rank === "number" ? c.rank : "·"}
																			</TableCell>
																			<TableCell>
																				<div className="font-medium">{c.name}</div>
																				{c.email ? (
																					<div className="text-xs text-muted-foreground">
																						{c.email}
																					</div>
																				) : null}
																				{c.summary ? (
																					<div className="mt-1 text-xs text-muted-foreground line-clamp-2">
																						{c.summary}
																					</div>
																				) : null}
																			</TableCell>
																			<TableCell>
																				<Badge
																					className={cn(
																						"border",
																						meta.badge
																					)}>
																					<Icon className="mr-1 h-3 w-3 inline-block align-sub" />
																					{meta.label}
																				</Badge>
																			</TableCell>
																			<TableCell className="text-right tabular-nums font-semibold">
																				{c.score}
																			</TableCell>
																			<TableCell className="text-right tabular-nums font-semibold">{formatNumber(vol.totalTickets)}</TableCell>
																			<TableCell className="text-right tabular-nums">{formatNumber(vol.closedCount)}</TableCell>
																			<TableCell className="text-right tabular-nums">{formatNumber(vol.openCount)}</TableCell>
																			<TableCell className="text-right tabular-nums">{formatNumber(vol.pendingCount)}</TableCell>
																			<TableCell className="text-right tabular-nums">{formatNumber(vol.reopenCount)}</TableCell>
																			<TableCell className="text-right tabular-nums">
																				<span className={ACCENT[corStatus(met.slaFirstResponsePct)].kpi}>
																					{formatPercent(met.slaFirstResponsePct, 0)}
																				</span>
																			</TableCell>
																			<TableCell className="text-right tabular-nums">
																				<span className={ACCENT[corStatus(met.slaResolutionPct)].kpi}>
																					{formatPercent(met.slaResolutionPct, 0)}
																				</span>
																			</TableCell>
																			<TableCell className="text-right tabular-nums">
																				<span className={ACCENT[corStatusInvert(met.reopenPct, { badAbove: 15, warnAbove: 8 })].kpi}>
																					{formatPercent(met.reopenPct, 0)}
																				</span>
																			</TableCell>
																			<TableCell className="text-right tabular-nums text-slate-600 dark:text-slate-400">
																				{formatNumber(vol.transfersIn)}<span className="mx-0.5 opacity-60">/</span>{formatNumber(vol.transfersOut)}
																			</TableCell>
																			<TableCell className="text-right tabular-nums text-slate-600 dark:text-slate-400">
																				{formatNumber(vol.messagesFromMe)}
																			</TableCell>
																			<TableCell className="text-right tabular-nums text-slate-600 dark:text-slate-400">
																				{formatMinutes(met.avgResolutionMinutes)}
																			</TableCell>
																		</TableRow>
																	);
																})}
															</TableBody>
														</Table>
													</div>
												) : (
													<EmptyState>Nenhum atendente com atividade no período selecionado.</EmptyState>
												)}
											</>
										)}
									</Panel>
								</>
							);
						})()}
					</TabsContent>
				</Tabs>
			</div>
		</MainContainer>
		</div>
		</TooltipProvider>
	);
};

export default Reports;
